import { useEffect, useState, useRef } from 'react';
import { useLayoutEffect } from 'react';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useSucursal } from '../../utils/sucursalContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { applyBrandingTheme, getContrastText } from '../../lib/brandingTheme';
import AccessControlSettings from '../access-control/AccessControlSettings';
import FintocSettings from '../payments/FintocSettings';
import FlowSettings from '../payments/FlowSettings';
import OperationalModelSettings from '../smart-occupancy/OperationalModelSettings';
import StaffFaceCaptureButton from '../access-control/StaffFaceCaptureButton';
import FlexPassSettings from '../smart-occupancy/FlexPassSettings';
import CoachPlansSettings from '../coaches/CoachPlansSettings';
import GuestSettings from '../guests/GuestSettings';
import MobileVersionsSettings from '../mobile/MobileVersionsSettings';

// Notificaciones reales: se cargan desde Supabase (ver estado dentro del componente).

// Feature flags (admin portal). Keep disabled until SMS/WhatsApp is implemented end-to-end.
const ENABLE_SMS_WHATSAPP_SETTINGS = false;
// Keep hidden until we implement them end-to-end.
const ENABLE_REGISTRO_ACTIVIDAD_SETTINGS = false;
const ENABLE_APIS_EXTERNAS_SETTINGS = false;
const ENABLE_RESPALDOS_SETTINGS = false;
const MOWE_DEFAULT_PRIMARY = '#314a25';
const MOWE_DEFAULT_SECONDARY = '#bfa98d';
const MOWE_DEFAULT_BG_LIGHT = '#f3f0ea';
const MOWE_DEFAULT_BG_DARK = '#0b1220';
const MOWE_DEFAULT_SURFACE_LIGHT = '#ffffff';
const MOBILE_DEFAULT_PRIMARY = '#314a25';
const MOBILE_DEFAULT_SECONDARY = '#9b7653';
const MOBILE_DEFAULT_BG_LIGHT = '#f3f1ed';
const MOBILE_DEFAULT_BG_DARK = '#0b1220';
const SIDEBAR_STORAGE_KEY = 'gymadmin_sidebar_open';
const MOBILE_BREAKPOINT = 768;
const MOBILE_HOME_IMAGE_MAX_BYTES = 500 * 1024;
const DEFAULT_WELCOME_SUBJECT = 'Bienvenido a {{gimnasio}} - Tu clave temporal';
const DEFAULT_WELCOME_HTML = `
<div style="margin:0;padding:24px;background:#e9f3f2;font-family:Arial,Helvetica,sans-serif;color:#0f172a;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:620px;margin:0 auto;background:#ffffff;border-radius:18px;overflow:hidden;box-shadow:0 8px 28px rgba(15,23,42,.08);">
    <tr>
      <td style="padding:34px 28px;text-align:center;background:linear-gradient(135deg,#18b2a7 0%,#33c7bb 100%);color:#ffffff;">
        <div style="display:inline-block;background:rgba(255,255,255,.18);padding:12px 16px;border-radius:999px;font-size:22px;line-height:1;">💪</div>
        <h1 style="margin:16px 0 8px;font-size:34px;line-height:1.1;font-weight:800;color:#ffffff;">¡Bienvenido a {{gimnasio}}!</h1>
        <p style="margin:0;font-size:16px;line-height:1.5;color:#e6fffb;">Tu cuenta ya esta activa y lista para usar.</p>
      </td>
    </tr>
    <tr>
      <td style="padding:30px 32px 10px;">
        <h2 style="margin:0 0 10px;font-size:28px;line-height:1.1;color:#0f172a;">Hola {{nombre}} 👋</h2>
        <p style="margin:0 0 20px;font-size:17px;line-height:1.6;color:#334155;">Ingresa a la app movil con estos datos y luego cambia tu clave temporal para continuar.</p>
      </td>
    </tr>
    <tr>
      <td style="padding:0 32px 18px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border:1px solid #dbe3ef;border-radius:12px;overflow:hidden;background:#f8fafc;">
          <tr>
            <td style="padding:14px 16px 8px;font-size:13px;color:#64748b;">Email</td>
          </tr>
          <tr>
            <td style="padding:0 16px 14px;font-size:18px;font-weight:700;color:#0f172a;">{{email}}</td>
          </tr>
          <tr>
            <td style="padding:14px 16px 8px;font-size:13px;color:#64748b;border-top:1px solid #e2e8f0;">Clave temporal</td>
          </tr>
          <tr>
            <td style="padding:0 16px 14px;font-size:20px;font-weight:800;color:#0f172a;letter-spacing:.3px;">{{clave_temporal}}</td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:0 32px 28px;">
        <div style="padding:14px 16px;border-radius:12px;background:#fff3cd;border:1px solid #ffe69c;color:#7a4a00;font-size:14px;line-height:1.5;">
          <strong>Importante:</strong> esta clave es temporal. Debes cambiarla desde la app movil en tu primer ingreso.
        </div>
      </td>
    </tr>
    <tr>
      <td style="padding:18px 24px;text-align:center;background:#0b1b3a;color:#9fb0d0;">
        <p style="margin:0;font-size:13px;line-height:1.5;">Si no solicitaste esta cuenta, ignora este correo.</p>
        <p style="margin:6px 0 0;font-size:12px;line-height:1.4;">© {{gimnasio}}</p>
      </td>
    </tr>
  </table>
</div>
`.trim();

const compressMobileHomeImage = async (file: File): Promise<Blob> => {
  if (!file.type.startsWith('image/')) throw new Error('Selecciona una imagen válida.');
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('No se pudo procesar la imagen.');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const encode = (quality: number) => new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('No se pudo comprimir la imagen.')), 'image/webp', quality);
  });
  for (const quality of [0.86, 0.76, 0.66, 0.56, 0.46]) {
    const blob = await encode(quality);
    if (blob.size <= MOBILE_HOME_IMAGE_MAX_BYTES) return blob;
  }
  throw new Error('La imagen sigue superando 500 KB después de optimizarla. Usa una imagen más simple.');
};

const configuracionOpciones = [
  {
    categoria: 'General',
    items: [
      { id: 'datos-gimnasio', icon: 'ri-building-line', titulo: 'Datos del Gimnasio', descripcion: 'Nombre, dirección, teléfono y logo' },
      { id: 'personalizacion', icon: 'ri-palette-line', titulo: 'Personalización', descripcion: 'Colores, tema y apariencia de la app' },
      { id: 'reglamento', icon: 'ri-file-list-3-line', titulo: 'Reglamento', descripcion: 'Configurar reglas y aceptación obligatoria en app móvil' },
      { id: 'modelo-operativo', icon: 'ri-pulse-line', titulo: 'Modelo Operativo', descripcion: 'Reservas, ocupación y capacidad por sucursal' },
      { id: 'flex-pass', icon: 'ri-price-tag-3-line', titulo: 'Flex Pass', descripcion: 'Ofertas y precios dinámicos por ocupación' },
      { id: 'planes-coaches', icon: 'ri-team-line', titulo: 'Planes de Coaches', descripcion: 'Planes, límites y vigencia de coaches externos' },
      { id: 'invitados', icon: 'ri-user-add-line', titulo: 'Invitados', descripcion: 'Cantidad, vigencia y reglas de acompañamiento' },
      { id: 'versiones-moviles', icon: 'ri-smartphone-line', titulo: 'Versiones móviles', descripcion: 'Publicación y actualización obligatoria de iOS y Android' },
      { id: 'idioma-region', icon: 'ri-global-line', titulo: 'Idioma y Región', descripcion: 'Zona horaria, formato de fecha y moneda' },
      { id: 'centro-ayuda', icon: 'ri-question-answer-line', titulo: 'Centro de ayuda', descripcion: 'Guías rápidas según tu perfil' }
    ]
  },
    {
      categoria: 'Notificaciones',
      items: [
        { id: 'alertas-push', icon: 'ri-notification-badge-line', titulo: 'Alertas Push', descripcion: 'Configurar notificaciones a usuarios' },
        { id: 'correos-automaticos', icon: 'ri-mail-settings-line', titulo: 'Correos Automáticos', descripcion: 'Plantillas de email y recordatorios' },
        ...(ENABLE_SMS_WHATSAPP_SETTINGS
          ? [{ id: 'sms-whatsapp', icon: 'ri-message-2-line', titulo: 'SMS y WhatsApp', descripcion: 'Mensajes de confirmación y avisos' }]
          : []),
      ]
    },
  {
    categoria: 'Pagos',
    items: [
      { id: 'metodos-pago', icon: 'ri-bank-card-line', titulo: 'Métodos de Pago', descripcion: 'Tarjetas, transferencias y efectivo' },
      { id: 'comisiones', icon: 'ri-percent-line', titulo: 'Comisiones', descripcion: 'Configurar tasas y recargos' },
    ]
  },
  {
    categoria: 'Seguridad',
    items: [
      { id: 'usuarios-admin', icon: 'ri-shield-user-line', titulo: 'Usuarios Admin', descripcion: 'Gestionar accesos del personal' },
      { id: 'contrasenas', icon: 'ri-lock-password-line', titulo: 'Contraseñas', descripcion: 'Políticas de seguridad' },
      ...(ENABLE_REGISTRO_ACTIVIDAD_SETTINGS
        ? [{ id: 'registro-actividad', icon: 'ri-history-line', titulo: 'Registro de Actividad', descripcion: 'Historial de acciones del sistema' }]
        : [])
    ]
  },
  {
    categoria: 'Integraciones',
    items: [
      { id: 'control-acceso', icon: 'ri-qr-code-line', titulo: 'Control de Acceso', descripcion: 'QR, torniquetes y lectores' },
      ...(ENABLE_APIS_EXTERNAS_SETTINGS
        ? [{ id: 'apis-externas', icon: 'ri-apps-line', titulo: 'APIs Externas', descripcion: 'Conexiones con otros servicios' }]
        : []),
      ...(ENABLE_RESPALDOS_SETTINGS
        ? [{ id: 'respaldos', icon: 'ri-database-2-line', titulo: 'Respaldos', descripcion: 'Backup automático de datos' }]
        : [])
    ]
  }
];

type HelpAudience = 'manager' | 'coach_interno' | 'coach_externo' | 'recepcionista' | 'nutricionista';
type HelpGuide = {
  id: string;
  title: string;
  description: string;
  icon: string;
  audience: HelpAudience[];
  path?: string;
  steps: string[];
  note?: string;
};

const HELP_GUIDES: HelpGuide[] = [
  {
    id: 'usuarios', title: 'Crear y habilitar usuarios', description: 'Registro, revisión y activación de una cuenta.', icon: 'ri-user-add-line',
    audience: ['manager', 'recepcionista'], path: '/usuarios',
    steps: ['Abre Usuarios y revisa si la persona ya tiene una cuenta.', 'Crea el usuario o revisa su inscripción pendiente.', 'Confirma sus datos, plan y estado del pago.', 'Si no existe pago asociado, habilita únicamente después de aceptar la advertencia.', 'Comprueba que el rostro quede enrolado antes de validar el acceso.'],
    note: 'Evita crear una segunda cuenta para el mismo correo o persona.',
  },
  {
    id: 'membresias', title: 'Membresías, pagos y renovación', description: 'Registrar pagos y gestionar la vigencia del socio.', icon: 'ri-bank-card-line',
    audience: ['manager', 'recepcionista'], path: '/pagos',
    steps: ['Busca al usuario antes de registrar el pago.', 'Selecciona solamente un medio de pago habilitado para la sucursal.', 'Confirma plan, monto y meses contratados.', 'Revisa que el pago aparezca completado y que la membresía quede activa.', 'Gestiona renovación o término desde la membresía del usuario.'],
    note: 'No registres nuevamente un pago digital que ya fue confirmado por el proveedor.',
  },
  {
    id: 'coach-clientes', title: 'Coach interno: clientes propios', description: 'Rutinas, seguimiento y evaluaciones.', icon: 'ri-heart-pulse-line',
    audience: ['manager', 'coach_interno'], path: '/progreso',
    steps: ['Entra a Mis Clientes y selecciona un usuario asignado.', 'Revisa condición física, progreso y rutina actual.', 'Presiona Asignar rutina y elige una rutina disponible.', 'Confirma la asignación: el sistema crea una copia independiente para ese usuario.', 'Programa evaluaciones o el siguiente cambio de rutina y revisa el historial.'],
    note: 'El coach solo puede gestionar usuarios vinculados a su cuenta.',
  },
  {
    id: 'coach-externos', title: 'Clientes vinculados a un coach', description: 'Invitación, aprobación, reserva y acceso.', icon: 'ri-team-line',
    audience: ['manager', 'coach_interno', 'coach_externo'], path: '/progreso',
    steps: ['Genera la invitación o QR autorizado para el cliente.', 'El cliente completa sus datos y captura facial.', 'Administración revisa y aprueba la solicitud.', 'El cliente reserva un bloque de atención con su coach.', 'El acceso se autoriza solo durante la reserva y mientras el coach esté presente.'],
    note: 'Cada acceso queda registrado para trazabilidad y estadísticas.',
  },
  {
    id: 'rutinas', title: 'Crear y asignar rutinas', description: 'Rutina base, copia por usuario y familias.', icon: 'ri-file-list-3-line',
    audience: ['manager', 'coach_interno'], path: '/rutinas',
    steps: ['Crea una rutina y agrega sus sesiones y ejercicios.', 'Guarda la rutina base.', 'Desde Rutinas usa Asignar, o desde Mis Clientes usa Asignar rutina.', 'Selecciona al usuario y confirma.', 'Edita la copia del usuario cuando necesite una adaptación individual.'],
    note: 'Modificar la rutina base no altera las copias que ya fueron asignadas.',
  },
  {
    id: 'reservas', title: 'Reservas y horarios', description: 'Clases, cupos y reglas de anticipación.', icon: 'ri-calendar-check-line',
    audience: ['manager', 'recepcionista', 'coach_interno', 'coach_externo'], path: '/horarios',
    steps: ['Abre Horarios y Cupos.', 'Selecciona la fecha y revisa las clases publicadas.', 'Comprueba capacidad, inscritos y horario de cierre.', 'Registra o cancela la reserva según corresponda.', 'Confirma que el cambio se refleje en la aplicación del socio.'],
  },
  {
    id: 'ventas', title: 'Venta presencial y cierre', description: 'Productos, medios digitales y cierre de jornada.', icon: 'ri-shopping-cart-2-line',
    audience: ['manager', 'recepcionista'], path: '/ventas',
    steps: ['Abre Ventas y agrega los productos.', 'Selecciona un medio habilitado y confirma el cobro.', 'Verifica que la venta figure como completada.', 'Revisa el historial y las anulaciones antes del cierre.', 'Cierra la jornada; el sistema consolida los medios digitales y envía el resumen configurado.'],
  },
  {
    id: 'rostro', title: 'Rostro y control de acceso', description: 'Captura, enrolamiento y diagnóstico básico.', icon: 'ri-scan-2-line',
    audience: ['manager', 'recepcionista'], path: '/usuarios',
    steps: ['Busca al usuario y confirma que sea la cuenta correcta.', 'Revisa si ya existe un rostro registrado.', 'Captura una nueva imagen solo cuando realmente falte.', 'Comprueba el estado de sincronización de las cámaras.', 'Si existe rechazo, revisa membresía, liberación de pago y permisos antes de repetir la captura.'],
  },
  {
    id: 'nutricion', title: 'Seguimiento nutricional', description: 'Evaluaciones, cálculos y observaciones del usuario.', icon: 'ri-heart-add-line',
    audience: ['manager', 'nutricionista'], path: '/progreso',
    steps: ['Selecciona al usuario asignado.', 'Revisa sus antecedentes y condición física declarada.', 'Registra la evaluación y las mediciones.', 'Guarda recomendaciones y documentos correspondientes.', 'Consulta el historial antes de realizar una nueva actualización.'],
  },
];

const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'] as const;
type DiaSemana = (typeof DIAS_SEMANA)[number];
type HorarioDia = { apertura: string; cierre: string; cerrado: boolean };
type HorariosSucursal = Record<DiaSemana, HorarioDia>;
type SucursalForm = {
  nombre: string;
  estado: 'activo' | 'mantenimiento' | 'inactivo';
  direccion: string;
  telefono: string;
  email: string;
  capacidad_maxima: string;
  horarios: HorariosSucursal;
};

type PagoProviderSecretsState = {
  commerceCode: string;
  childCommerceCode: string;
  apiKeySecret: string;
  configured: boolean;
  maskedCommerceCode: string;
  maskedChildCommerceCode: string;
  maskedApiKeySecret: string;
  loading: boolean;
  error: string | null;
};

type MercadoPagoSecretsState = {
  publicKey: string;
  accessToken: string;
  webhookSecret: string;
  configured: boolean;
  maskedPublicKey: string;
  maskedAccessToken: string;
  maskedWebhookSecret: string;
  webhookUrl: string;
  loading: boolean;
  error: string | null;
};

const buildDefaultHorarios = (): HorariosSucursal =>
  Object.fromEntries(
    DIAS_SEMANA.map((dia) => [dia, { apertura: '06:00', cierre: '22:00', cerrado: false }])
  ) as HorariosSucursal;

const buildSucursalForm = (sucursal?: any): SucursalForm => {
  const baseHorarios = buildDefaultHorarios();
  const raw = (sucursal?.horarios as Record<string, HorarioDia> | undefined) || {};

  for (const dia of DIAS_SEMANA) {
    if (raw[dia]) {
      baseHorarios[dia] = {
        apertura: raw[dia].apertura || '06:00',
        cierre: raw[dia].cierre || '22:00',
        cerrado: !!raw[dia].cerrado,
      };
    }
  }

  return {
    nombre: sucursal?.nombre || '',
    estado: (sucursal?.estado || 'activo') as 'activo' | 'mantenimiento' | 'inactivo',
    direccion: sucursal?.direccion || '',
    telefono: sucursal?.telefono || '',
    email: sucursal?.email || '',
    capacidad_maxima: String(sucursal?.capacidad_maxima ?? 150),
    horarios: baseHorarios,
  };
};

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { sucursalActiva, sucursales: sucursalesDB, cambiarSucursal, refreshSucursales } = useSucursal();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (saved === '1') return true;
    if (saved === '0') return false;
    return window.innerWidth >= MOBILE_BREAKPOINT;
  });
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeConfigModal, setActiveConfigModal] = useState<string | null>(null);
  const [helpSearch, setHelpSearch] = useState('');
  const [expandedHelpId, setExpandedHelpId] = useState<string | null>(null);
  const [showSucursalSelector, setShowSucursalSelector] = useState(false);
  const [showSucursalesModal, setShowSucursalesModal] = useState(false);
  const [showEditSucursalModal, setShowEditSucursalModal] = useState(false);
  const [showNewSucursalModal, setShowNewSucursalModal] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState<any>(null);
  const [sucursalForm, setSucursalForm] = useState<SucursalForm>(() => buildSucursalForm());
  const [showSidebarUserMenu, setShowSidebarUserMenu] = useState(false);
  const [deletingSucursalId, setDeletingSucursalId] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const mobileHomeImageInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
  const location = useLocation();
  const isEmbedView = new URLSearchParams(location.search).get('embed') === '1';

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobileViewport(mobile);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) return;
    setSidebarOpen(false);
  }, [isMobileViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isMobileViewport) return;
    localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarOpen ? '1' : '0');
  }, [sidebarOpen, isMobileViewport]);

  type NotificacionRow = {
    id: string;
    gimnasio_id: string;
    usuario_id: string | null;
    destinatario_admin_id?: string | null;
    destino: 'admin' | 'usuario';
    tipo: string;
    titulo: string;
    mensaje: string;
    meta: any;
    leida: boolean;
    created_at: string;
  };

  type NotificacionUI = {
    id: string;
    tipo: string;
    titulo: string;
    mensaje: string;
    tiempo: string;
    leida: boolean;
    created_at: string;
  };

  const [notificaciones, setNotificaciones] = useState<NotificacionUI[]>([]);
  const [notificacionesLoading, setNotificacionesLoading] = useState(false);
  const [notificacionesError, setNotificacionesError] = useState<string | null>(null);
  const [marcandoTodasNotificaciones, setMarcandoTodasNotificaciones] = useState(false);
  const [familyProposalPendingCount, setFamilyProposalPendingCount] = useState(0);
  const activeProposalGymId = profile?.gimnasioId || sucursalActiva?.gimnasio_id || null;
  const activeProfileRole = profile?.role || '';
  const activeProfileStaffType = profile?.tipoStaff || '';
  const unreadCount = useMemo(() => notificaciones.filter((n) => !n.leida).length, [notificaciones]);
  const chatUnreadCount = useMemo(() => notificaciones.filter((n) => !n.leida && n.tipo === 'chat').length, [notificaciones]);

  useEffect(() => {
    if (!activeProfileRole) return;
    const gimnasioId = activeProposalGymId;
    const canReview = activeProfileRole === 'platform_admin'
      || ['admin', 'gym_admin'].includes(activeProfileRole)
      || (activeProfileRole === 'staff' && ['coach_interno', 'coach_externo'].includes(activeProfileStaffType));
    if (!canReview || (!gimnasioId && activeProfileRole !== 'platform_admin')) {
      setFamilyProposalPendingCount(0);
      return;
    }
    const loadPending = async () => {
      let query = supabase.from('rutina_familia_recomendaciones_ia')
        .select('id', { count: 'exact', head: true }).eq('tipo_propuesta', 'familia').eq('estado', 'pendiente');
      if (gimnasioId) query = query.eq('gimnasio_id', gimnasioId);
      const { count, error } = await query;
      if (!error) setFamilyProposalPendingCount(count || 0);
    };
    void loadPending();
    const channel = supabase.channel(`family-proposal-count:${gimnasioId || 'all'}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'rutina_familia_recomendaciones_ia',
        ...(gimnasioId ? { filter: `gimnasio_id=eq.${gimnasioId}` } : {}),
      } as any, () => void loadPending())
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [activeProfileRole, activeProfileStaffType, activeProposalGymId]);

  // Estados para configuración
  const [configData, setConfigData] = useState({
    // Datos del gimnasio
    nombreGimnasio: 'PowerFit Gym',
    direccion: 'Av. Principal 123, Santiago',
    telefono: '+56 9 8765 4321',
    email: 'info@powerfit.cl',
    sitioWeb: 'www.powerfit.cl',
    
    // Personalización
    colorPrimario: MOWE_DEFAULT_PRIMARY,
    colorSecundario: MOWE_DEFAULT_SECONDARY,
    colorFondoClaro: MOWE_DEFAULT_BG_LIGHT,
    colorFondoOscuro: MOWE_DEFAULT_BG_DARK,
    colorSuperficieClaro: MOWE_DEFAULT_SURFACE_LIGHT,
    mobileColorPrimario: MOBILE_DEFAULT_PRIMARY,
    mobileColorSecundario: MOBILE_DEFAULT_SECONDARY,
    mobileColorFondoClaro: MOBILE_DEFAULT_BG_LIGHT,
    mobileColorFondoOscuro: MOBILE_DEFAULT_BG_DARK,
    mobileTemaOscuro: false,
    reglamentoHabilitado: false,
    reglamentoTitulo: 'Reglamento del gimnasio',
    reglamentoContenido: '',
    reglamentoVersion: 1,
    logoUrl: '',
    mobileHomeImageUrl: '',
    temaOscuro: false,
    
    // Idioma y región
    idioma: 'es',
    zonaHoraria: 'America/Santiago',
    moneda: 'CLP',
    formatoFecha: 'dd/MM/yyyy',
    
    // Notificaciones
    pushReservas: true,
    pushPagos: true,
    pushCancelaciones: true,
    emailBienvenida: true,
    emailBienvenidaSubject: DEFAULT_WELCOME_SUBJECT,
    emailBienvenidaHtml: DEFAULT_WELCOME_HTML,
    emailBienvenidaVersion: 1,
    emailRecordatorios: true,
    emailVencimientos: true,
    smsConfirmaciones: false,
    whatsappAvisos: false,
    
    // Pagos
    efectivo: true,
    tarjetaCredito: true,
    tarjetaDebito: true,
    transferencia: true,
    otroMetodo: true,
    comisionTarjeta: 2.9,
    comisionTransferencia: 0,
    datosFacturacion: false,
    posJornadaActiva: true,
    posArqueoEfectivo: true,
    posEmailCierreActivo: false,

    // Pagos online (checkout)
    onlineActivo: false,
    onlineProveedor: 'transbank_webpay_plus',
    onlineModo: 'sandbox' as 'sandbox' | 'production',
    transbankWebpayActivo: false,
    transbankOneclickActivo: false,
    mercadoPagoActivo: false,
    fintocActivo: false,
    flowActivo: false,
    
    // Seguridad
    require2FA: false,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    passwordTemporalUsuarios: '',
    
    // Integraciones
    qrAccess: false,
    apiKey: '',
    backupAutomatico: true,
    backupFrecuencia: 'diario'
  });

  const [savingConfig, setSavingConfig] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState('');
  const [emailTestSending, setEmailTestSending] = useState(false);
  const [pagoProviderSecrets, setPagoProviderSecrets] = useState<PagoProviderSecretsState>({
    commerceCode: '',
    childCommerceCode: '',
    apiKeySecret: '',
    configured: false,
    maskedCommerceCode: '',
    maskedChildCommerceCode: '',
    maskedApiKeySecret: '',
    loading: false,
    error: null,
  });
  const [oneclickProviderSecrets, setOneclickProviderSecrets] = useState<PagoProviderSecretsState>({
    commerceCode: '', childCommerceCode: '', apiKeySecret: '', configured: false,
    maskedCommerceCode: '', maskedChildCommerceCode: '', maskedApiKeySecret: '', loading: false, error: null,
  });
  const [mercadoPagoSecrets, setMercadoPagoSecrets] = useState<MercadoPagoSecretsState>({
    publicKey: '', accessToken: '', webhookSecret: '', configured: false, maskedPublicKey: '', maskedAccessToken: '', maskedWebhookSecret: '', webhookUrl: '', loading: false, error: null,
  });
  const [paymentConnectionTest, setPaymentConnectionTest] = useState<Record<string, { loading: boolean; message: string; ok: boolean }>>({});
  const [editingWebpayCredentials, setEditingWebpayCredentials] = useState(false);

  const testPaymentConnection = async (provider: string) => {
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) return;
    setPaymentConnectionTest((prev) => ({ ...prev, [provider]: { loading: true, message: 'Comprobando conexión…', ok: false } }));
    try {
      const { data, error } = await supabase.functions.invoke('payments-provider-secrets', {
        body: { action: 'test', gimnasio_id: gimnasioId, proveedor: provider, modo: configData.onlineModo },
      });
      let message = String(data?.message || data?.error || '');
      if (error) {
        const response = (error as { context?: Response }).context;
        if (response) {
          try { const body = await response.clone().json(); message = String(body?.error || message); } catch { /* respuesta no JSON */ }
        }
        throw new Error(message || 'No se pudo verificar la conexión.');
      }
      if (!data?.ok) throw new Error(message || 'No se pudo verificar la conexión.');
      setPaymentConnectionTest((prev) => ({ ...prev, [provider]: { loading: false, message: `${message} (${data.latency_ms} ms)`, ok: true } }));
      await loadPagoProviderSecrets(provider, configData.onlineModo, gimnasioId);
    } catch (error) {
      setPaymentConnectionTest((prev) => ({ ...prev, [provider]: { loading: false, message: error instanceof Error ? error.message : 'No se pudo verificar la conexión.', ok: false } }));
    }
  };

  const handleResetBrandingDefaults = () => {
    setConfigData((prev) => ({
      ...prev,
      colorPrimario: MOWE_DEFAULT_PRIMARY,
      colorSecundario: MOWE_DEFAULT_SECONDARY,
      colorFondoClaro: MOWE_DEFAULT_BG_LIGHT,
      colorFondoOscuro: MOWE_DEFAULT_BG_DARK,
      colorSuperficieClaro: MOWE_DEFAULT_SURFACE_LIGHT,
      mobileColorPrimario: MOBILE_DEFAULT_PRIMARY,
      mobileColorSecundario: MOBILE_DEFAULT_SECONDARY,
      mobileColorFondoClaro: MOBILE_DEFAULT_BG_LIGHT,
      mobileColorFondoOscuro: MOBILE_DEFAULT_BG_DARK,
      mobileTemaOscuro: false,
      temaOscuro: false,
    }));
  };

  const getConfigStorageKey = () => {
    const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId || 'sin-gimnasio';
    const sucursalId = sucursalActiva?.id || 'sin-sucursal';
    return `gymadmin_config_${gimnasioId}_${sucursalId}`;
  };

  const getCurrentGymId = () => (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId || null;

  const handleSendWelcomeTestEmail = async () => {
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) {
      alert('No se pudo resolver el gimnasio para enviar correo de prueba.');
      return;
    }

    const targetEmail = emailTestTo.trim().toLowerCase();
    if (!targetEmail || !targetEmail.includes('@')) {
      alert('Ingresa un email válido para la prueba.');
      return;
    }

    try {
      setEmailTestSending(true);
      const vars = {
        nombre: 'Usuario Prueba',
        email: targetEmail,
        clave_temporal: 'Tmp1234!9a',
        gimnasio: configData.nombreGimnasio || 'Tu gimnasio',
      };

      const { data, error } = await supabase.functions.invoke('email-template-test', {
        body: {
          gimnasio_id: gimnasioId,
          tipo: 'bienvenida',
          to_email: targetEmail,
          template_subject: configData.emailBienvenidaSubject || DEFAULT_WELCOME_SUBJECT,
          template_html: configData.emailBienvenidaHtml || DEFAULT_WELCOME_HTML,
          template_activo: !!configData.emailBienvenida,
          vars,
        },
      });

      if (error) {
        alert(error.message || 'No se pudo enviar el correo de prueba.');
        return;
      }

      if ((data as any)?.error) {
        const details = String((data as any)?.details || '').trim();
        alert(details ? `${String((data as any).error)}: ${details}` : String((data as any).error));
        return;
      }

      alert(`Correo de prueba enviado a ${targetEmail}.`);
    } catch (err: any) {
      alert(err?.message || 'No se pudo enviar el correo de prueba.');
    } finally {
      setEmailTestSending(false);
    }
  };

  const formatTiempoRelativo = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';

    const diffMs = Date.now() - d.getTime();
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));
    const diffMin = Math.floor(diffSec / 60);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    if (diffSec < 60) return 'Hace 1 min';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    if (diffH < 24) return `Hace ${diffH} h`;
    if (diffD === 1) return 'Ayer';
    return `Hace ${diffD} días`;
  };

  const loadNotificaciones = async () => {
    const gimnasioId = getCurrentGymId();
    if (!profile) return;
    if (!gimnasioId && profile.role !== 'platform_admin') return;

    try {
      setNotificacionesLoading(true);
      setNotificacionesError(null);

      let q = supabase
        .from('notificaciones')
        .select('id,gimnasio_id,usuario_id,destinatario_admin_id,destino,tipo,titulo,mensaje,meta,leida,created_at')
        .eq('destino', 'admin')
        .order('created_at', { ascending: false })
        .limit(20);

      if (gimnasioId) q = q.eq('gimnasio_id', gimnasioId);
      if (profile.role === 'staff') {
        q = q.neq('tipo', 'pago');
        if (profile.adminId) q = q.eq('destinatario_admin_id', profile.adminId);
      }

      const { data, error } = await q;
      if (error) throw error;

      const rows = (data || []) as any as NotificacionRow[];
      setNotificaciones(
        rows.map((r) => ({
          id: r.id,
          tipo: r.tipo,
          titulo: r.titulo,
          mensaje: r.mensaje,
          leida: !!r.leida,
          created_at: r.created_at,
          tiempo: formatTiempoRelativo(r.created_at),
        }))
      );
    } catch (err: any) {
      console.error('Error cargando notificaciones:', err);
      setNotificacionesError(err?.message || 'No se pudieron cargar las notificaciones.');
      setNotificaciones([]);
    } finally {
      setNotificacionesLoading(false);
    }
  };

  const marcarNotificacionLeida = async (id: string) => {
    try {
      const { error } = await supabase.from('notificaciones').update({ leida: true }).eq('id', id);
      if (error) throw error;
      setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    } catch (err) {
      console.error('Error marcando notificación como leída:', err);
    }
  };

  const marcarTodasNotificacionesLeidas = async () => {
    const gimnasioId = getCurrentGymId();
    if (marcandoTodasNotificaciones || unreadCount === 0) return;

    try {
      setMarcandoTodasNotificaciones(true);
      setNotificacionesError(null);
      let query = supabase
        .from('notificaciones')
        .update({ leida: true })
        .eq('destino', 'admin')
        .eq('leida', false);
      if (gimnasioId) query = query.eq('gimnasio_id', gimnasioId);

      const { error } = await query;
      if (error) throw error;
      setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
    } catch (err: any) {
      console.error('Error marcando todas las notificaciones:', err);
      setNotificacionesError(err?.message || 'No se pudieron marcar las notificaciones.');
    } finally {
      setMarcandoTodasNotificaciones(false);
    }
  };

  // Load in background so the bell can show unread dot without needing to open the dropdown.
  useEffect(() => {
    void loadNotificaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.gimnasioId, (sucursalActiva as any)?.gimnasio_id]);

  // Realtime: push new admin notifications without requiring a refresh.
  useEffect(() => {
    if (!profile) return;

    const gimnasioId = getCurrentGymId();
    if (!gimnasioId && profile.role !== 'platform_admin') return;

    const baseInsert = {
      event: 'INSERT' as const,
      schema: 'public',
      table: 'notificaciones',
    };
    const baseUpdate = {
      event: 'UPDATE' as const,
      schema: 'public',
      table: 'notificaciones',
    };

    const insertFilter = gimnasioId ? { ...baseInsert, filter: `gimnasio_id=eq.${gimnasioId}` } : baseInsert;
    const updateFilter = gimnasioId ? { ...baseUpdate, filter: `gimnasio_id=eq.${gimnasioId}` } : baseUpdate;

    const channel = supabase
      .channel(`notificaciones_admin_${gimnasioId || 'all'}`)
      .on('postgres_changes', insertFilter as any, (payload: any) => {
        const n = payload?.new as NotificacionRow | undefined;
        if (!n) return;
        if (n.destino !== 'admin') return;
        if (profile.role === 'staff' && n.tipo === 'pago') return;
        if (gimnasioId && n.gimnasio_id !== gimnasioId) return;

        setNotificaciones((prev) => {
          if (prev.some((x) => x.id === n.id)) return prev;
          const next: NotificacionUI = {
            id: n.id,
            tipo: n.tipo,
            titulo: n.titulo,
            mensaje: n.mensaje,
            leida: !!n.leida,
            created_at: n.created_at,
            tiempo: formatTiempoRelativo(n.created_at),
          };
          return [next, ...prev].slice(0, 20);
        });
      })
      .on('postgres_changes', updateFilter as any, (payload: any) => {
        const n = payload?.new as Partial<NotificacionRow> | undefined;
        if (!n?.id) return;
        if (n.destino && n.destino !== 'admin') return;
        if (gimnasioId && n.gimnasio_id && n.gimnasio_id !== gimnasioId) return;

        if (typeof n.leida === 'boolean') {
          setNotificaciones((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: n.leida! } : x)));
        }
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, profile?.gimnasioId, (sucursalActiva as any)?.gimnasio_id]);

  type UsuarioAdminRow = {
    id: string;
    auth_id: string;
    email: string;
    nombre: string;
    telefono: string | null;
    foto_perfil_url: string | null;
    role: 'platform_admin' | 'gym_admin' | 'staff' | 'admin';
    tipo_staff: 'general' | 'recepcionista' | 'nutricionista' | 'coach_interno' | 'coach_externo';
    es_supervisor_coaches: boolean;
    clientes_externos_habilitados: boolean;
    clientes_externos_modalidad: 'coach_tarifa' | 'sin_cobro';
    clientes_externos_max_activos: number;
    clientes_externos_max_por_bloque: number;
    clientes_externos_minutos_antes: number;
    clientes_externos_minutos_despues: number;
    clientes_externos_presencia_max_horas: number;
    gimnasio_id: string | null;
    sucursal_id: string | null;
    activo: boolean;
    created_at: string;
  };

  const [usuariosAdmin, setUsuariosAdmin] = useState<UsuarioAdminRow[]>([]);
  const [usuariosAdminLoading, setUsuariosAdminLoading] = useState(false);
  const [usuariosAdminError, setUsuariosAdminError] = useState<string | null>(null);
  const [inviteAdminOpen, setInviteAdminOpen] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState<string | null>(null);
  const [resettingAdminAuthId, setResettingAdminAuthId] = useState<string | null>(null);
  const [editingAdminPhotoUploading, setEditingAdminPhotoUploading] = useState(false);
  const [editingAdminForm, setEditingAdminForm] = useState({
    nombre: '', email: '', telefono: '', foto_perfil_url: '', role: 'staff', tipo_staff: 'general', sucursal_id: '', es_supervisor_coaches: false,
    clientes_externos_habilitados: false, clientes_externos_modalidad: 'sin_cobro', clientes_externos_max_activos: 10,
    clientes_externos_max_por_bloque: 4, clientes_externos_minutos_antes: 15, clientes_externos_minutos_despues: 15,
    clientes_externos_presencia_max_horas: 12,
  });
  const [inviteAdminForm, setInviteAdminForm] = useState({
    email: '',
    nombre: '',
    telefono: '',
    role: 'staff' as 'gym_admin' | 'staff' | 'admin',
    tipo_staff: 'general' as 'general' | 'recepcionista' | 'nutricionista' | 'coach_interno' | 'coach_externo',
    es_supervisor_coaches: false,
    sucursal_id: '' as string,
  });

  const loadUsuariosAdmin = async () => {
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) {
      setUsuariosAdminError('No se pudo resolver el gimnasio.');
      setUsuariosAdmin([]);
      return;
    }

    try {
      setUsuariosAdminLoading(true);
      setUsuariosAdminError(null);

      const { data, error } = await supabase
        .from('usuarios_admin')
        .select('id,auth_id,email,nombre,telefono,foto_perfil_url,role,tipo_staff,es_supervisor_coaches,clientes_externos_habilitados,clientes_externos_modalidad,clientes_externos_max_activos,clientes_externos_max_por_bloque,clientes_externos_minutos_antes,clientes_externos_minutos_despues,clientes_externos_presencia_max_horas,gimnasio_id,sucursal_id,activo,created_at')
        .eq('gimnasio_id', gimnasioId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsuariosAdmin((data || []) as any);
    } catch (err: any) {
      setUsuariosAdminError(err?.message || 'No se pudieron cargar los usuarios admin.');
      setUsuariosAdmin([]);
    } finally {
      setUsuariosAdminLoading(false);
    }
  };

  const handleInviteAdmin = async () => {
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) {
      alert('No se pudo resolver el gimnasio.');
      return;
    }

    const email = inviteAdminForm.email.trim().toLowerCase();
    const nombre = inviteAdminForm.nombre.trim();
    if (!email || !email.includes('@')) {
      alert('Ingresa un email válido.');
      return;
    }

    const sucursalId = inviteAdminForm.sucursal_id.trim();

    const getValidAccessToken = async () => {
      let {
        data: { session },
      } = await supabase.auth.getSession();

      const now = Math.floor(Date.now() / 1000);
      const expiresAt = session?.expires_at ?? 0;
      const isExpiredOrNearExpiry = !session?.access_token || expiresAt <= now + 60;

      if (isExpiredOrNearExpiry) {
        const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr || !refreshed?.session?.access_token) {
          throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
        }
        session = refreshed.session;
      }

      return session.access_token;
    };

    const invokeInvite = async (accessToken: string) =>
      supabase.functions.invoke('invitar-admin-gimnasio', {
        body: {
          gimnasio_id: gimnasioId,
          email,
          nombre: nombre || null,
          telefono: inviteAdminForm.telefono.trim() || null,
          role: inviteAdminForm.role,
          tipo_staff: inviteAdminForm.role === 'staff' ? inviteAdminForm.tipo_staff : 'general',
          es_supervisor_coaches: inviteAdminForm.role === 'staff' && inviteAdminForm.tipo_staff === 'coach_interno' && inviteAdminForm.es_supervisor_coaches,
          sucursal_id: sucursalId ? sucursalId : null,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

    const parseInvokeError = (invokeError: any) => {
      const msg = String(invokeError?.message || '').toLowerCase();
      const status = invokeError?.context?.status;
      const body = invokeError?.context?.body || '';
      const bodyLc = String(body || '').toLowerCase();
      const invalidJwt =
        status === 401 ||
        msg.includes('invalid jwt') ||
        bodyLc.includes('invalid jwt') ||
        bodyLc.includes('auth session missing') ||
        bodyLc.includes('jwt');
      return { invalidJwt };
    };

    let accessToken = '';
    try {
      accessToken = await getValidAccessToken();
    } catch (e: any) {
      alert(e?.message || 'Sesion expirada. Vuelve a iniciar sesion.');
      return;
    }

    let { data, error } = await invokeInvite(accessToken);

    if (error && parseInvokeError(error).invalidJwt) {
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session?.access_token) {
        alert('Sesion expirada. Vuelve a iniciar sesion.');
        return;
      }
      const retry = await invokeInvite(refreshed.session.access_token);
      data = retry.data;
      error = retry.error;
    }

    if (error) {
      console.error('Error invitar-admin-gimnasio:', error);
      alert('No se pudo invitar al usuario.');
      return;
    }

    if ((data as any)?.error) {
      alert(String((data as any).error));
      return;
    }

    setInviteAdminOpen(false);
    setInviteAdminForm({ email: '', nombre: '', telefono: '', role: 'staff', tipo_staff: 'general', sucursal_id: '', es_supervisor_coaches: false });
    await loadUsuariosAdmin();
    const accessLink = (data as any)?.access_link as string | null;
    if (accessLink) {
      try {
        await navigator.clipboard.writeText(accessLink);
        alert('Invitación creada. Copié el link de acceso en tu portapapeles.');
      } catch {
        alert('Invitación creada.');
      }
    } else {
      alert('Invitación creada.');
    }
  };

  const handleUpdateUsuarioAdmin = async (authId: string, patch: { activo?: boolean; nombre?: string; email?: string; telefono?: string | null; foto_perfil_url?: string | null; role?: string; tipo_staff?: string; sucursal_id?: string | null; clientes_externos_habilitados?: boolean; clientes_externos_modalidad?: string; clientes_externos_max_activos?: number; clientes_externos_max_por_bloque?: number; clientes_externos_minutos_antes?: number; clientes_externos_minutos_despues?: number; clientes_externos_presencia_max_horas?: number }) => {
    const { data, error } = await supabase.functions.invoke('usuarios-admin-update', {
      body: {
        auth_id: authId,
        ...patch,
      },
    });

    if (error) {
      console.error('Error usuarios-admin-update:', error);
      alert('No se pudo actualizar el usuario admin.');
      return false;
    }

    if ((data as any)?.error) {
      alert(String((data as any).error));
      return false;
    }

    await loadUsuariosAdmin();
    if (authId === profile?.id) {
      setActiveConfigModal(null);
      setShowConfig(false);
      window.location.reload();
    }
    return true;
  };

  const openEditUsuarioAdmin = (ua: UsuarioAdminRow) => {
    setEditingAdminId(ua.id);
    setEditingAdminForm({
      nombre: ua.nombre || '', email: ua.email || '', telefono: ua.telefono || '', foto_perfil_url: ua.foto_perfil_url || '', role: ua.role,
      tipo_staff: ua.tipo_staff || 'general', sucursal_id: ua.sucursal_id || '', es_supervisor_coaches: ua.es_supervisor_coaches === true,
      clientes_externos_habilitados: ua.clientes_externos_habilitados === true,
      clientes_externos_modalidad: ua.clientes_externos_modalidad || 'sin_cobro',
      clientes_externos_max_activos: ua.clientes_externos_max_activos || 10,
      clientes_externos_max_por_bloque: ua.clientes_externos_max_por_bloque || 4,
      clientes_externos_minutos_antes: ua.clientes_externos_minutos_antes ?? 15,
      clientes_externos_minutos_despues: ua.clientes_externos_minutos_despues ?? 15,
      clientes_externos_presencia_max_horas: ua.clientes_externos_presencia_max_horas || 12,
    });
  };

  const handleEditingAdminPhoto = async (ua: UsuarioAdminRow, file?: File) => {
    if (!file || editingAdminPhotoUploading) return;
    if (!ua.gimnasio_id) {
      alert('No se pudo resolver el gimnasio del integrante del personal.');
      return;
    }
    setEditingAdminPhotoUploading(true);
    try {
      const optimized = await compressMobileHomeImage(file);
      const path = `${ua.gimnasio_id}/staff/${ua.id}/profile.webp`;
      const { error } = await supabase.storage
        .from('mobile-branding')
        .upload(path, optimized, {
          contentType: 'image/webp',
          upsert: true,
          cacheControl: '3600',
        });
      if (error) throw error;
      const publicUrl = supabase.storage.from('mobile-branding').getPublicUrl(path).data.publicUrl;
      setEditingAdminForm((current) => ({
        ...current,
        foto_perfil_url: `${publicUrl}?v=${Date.now()}`,
      }));
    } catch (error: any) {
      console.error('Error subiendo foto del personal:', error);
      alert(error?.message || 'No se pudo subir la fotografía del integrante del personal.');
    } finally {
      setEditingAdminPhotoUploading(false);
    }
  };

  const resetUsuarioAdminPassword = async (ua: UsuarioAdminRow) => {
    if (!window.confirm(`¿Generar una clave temporal para ${ua.nombre}? Su acceso anterior dejará de funcionar.`)) return;
    setResettingAdminAuthId(ua.auth_id);
    try {
      const { data, error } = await supabase.functions.invoke('usuarios-admin-update', {
        body: { auth_id: ua.auth_id, reset_password: true },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error(String((data as any).error));
      const temporaryPassword = String((data as any)?.temporary_password || '');
      if (!temporaryPassword) throw new Error('El servicio no devolvió la clave temporal.');
      try {
        await navigator.clipboard.writeText(temporaryPassword);
        alert(`Clave temporal generada para ${ua.nombre}:\n\n${temporaryPassword}\n\nLa clave fue copiada al portapapeles.`);
      } catch {
        alert(`Clave temporal generada para ${ua.nombre}:\n\n${temporaryPassword}`);
      }
    } catch (err: any) {
      alert(err?.message || 'No se pudo generar la clave temporal.');
    } finally {
      setResettingAdminAuthId(null);
    }
  };

  const saveEditUsuarioAdmin = async (ua: UsuarioAdminRow) => {
    const nombre = editingAdminForm.nombre.trim();
    const email = editingAdminForm.email.trim().toLowerCase();
    if (!nombre) return alert('Ingresa el nombre del usuario.');
    if (!email || !email.includes('@')) return alert('Ingresa un email válido.');
    const updated = await handleUpdateUsuarioAdmin(ua.auth_id, {
      nombre, email, telefono: editingAdminForm.telefono.trim() || null,
      foto_perfil_url: editingAdminForm.foto_perfil_url.trim() || null,
      role: editingAdminForm.role,
      tipo_staff: editingAdminForm.role === 'staff' ? editingAdminForm.tipo_staff : 'general',
      sucursal_id: editingAdminForm.sucursal_id || null,
      clientes_externos_habilitados: editingAdminForm.role === 'staff' && editingAdminForm.tipo_staff === 'coach_interno' && editingAdminForm.clientes_externos_habilitados,
      clientes_externos_modalidad: editingAdminForm.clientes_externos_modalidad,
      clientes_externos_max_activos: Number(editingAdminForm.clientes_externos_max_activos),
      clientes_externos_max_por_bloque: Number(editingAdminForm.clientes_externos_max_por_bloque),
      clientes_externos_minutos_antes: Number(editingAdminForm.clientes_externos_minutos_antes),
      clientes_externos_minutos_despues: Number(editingAdminForm.clientes_externos_minutos_despues),
      clientes_externos_presencia_max_horas: Number(editingAdminForm.clientes_externos_presencia_max_horas),
    });
    if (!updated) return;
    const shouldSupervise = editingAdminForm.role === 'staff'
      && editingAdminForm.tipo_staff === 'coach_interno'
      && editingAdminForm.es_supervisor_coaches;
    const { error: supervisorError } = await supabase.rpc('set_coach_supervisor', {
      p_admin_id: ua.id,
      p_es_supervisor: shouldSupervise,
    });
    if (supervisorError) {
      alert('La información se guardó, pero no se pudo actualizar la atribución de supervisor.');
      return;
    }
    await loadUsuariosAdmin();
    setEditingAdminId(null);
  };

  // Estados para el perfil del usuario
  const [profileData, setProfileData] = useState({
    nombre: profile?.name || 'Administrador',
    email: profile?.email || 'admin@tugym.com',
    telefono: '+56 9 8765 4321',
    cargo:
      profile?.role === 'platform_admin' || profile?.role === 'admin' || profile?.role === 'gym_admin'
        ? 'Administrador'
        : 'Staff',
    fechaIngreso: '2023-01-15',
    avatar: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    if (!profile?.adminId) return;
    let cancelled = false;
    const loadProfile = async () => {
      const { data, error } = await supabase
        .from('usuarios_admin')
        .select('nombre,email,telefono,foto_perfil_url,created_at')
        .eq('id', profile.adminId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      setProfileData((current) => ({
        ...current,
        nombre: data.nombre || current.nombre,
        email: data.email || current.email,
        telefono: data.telefono || '',
        fechaIngreso: data.created_at || current.fechaIngreso,
        avatar: data.foto_perfil_url || '',
      }));
    };
    void loadProfile();
    return () => { cancelled = true; };
  }, [profile?.adminId]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isGymExemptFromSaas, setIsGymExemptFromSaas] = useState<boolean | null>(null);

  const [profileTab, setProfileTab] = useState<'info' | 'password'>('info');

  useEffect(() => {
    const key = getConfigStorageKey();
    let scopedConfig: Partial<typeof configData> = {};
    try {
      const raw = localStorage.getItem(key);
      if (raw) scopedConfig = JSON.parse(raw);
    } catch {
      scopedConfig = {};
    }

    const gimnasioId = getCurrentGymId();
    let cancelled = false;

    // Avoid flicker of "Pagar Servicio" while gym flags are still loading.
    setIsGymExemptFromSaas(null);

    const run = async () => {
      // Best-effort: apply cached theme immediately to avoid flashing defaults while we fetch DB branding.
      try {
        if (gimnasioId) {
          const cachedGym = localStorage.getItem(`gymadmin_branding_${gimnasioId}`);
          if (cachedGym) applyBrandingTheme(JSON.parse(cachedGym));
        }

        const cachedScoped: any = scopedConfig || {};
        if (
          cachedScoped.colorPrimario ||
          cachedScoped.colorSecundario ||
          cachedScoped.colorFondoClaro ||
          cachedScoped.colorFondoOscuro ||
          cachedScoped.colorSuperficieClaro ||
          typeof cachedScoped.temaOscuro === 'boolean'
        ) {
          applyBrandingTheme({
            colorPrimario: cachedScoped.colorPrimario,
            colorSecundario: cachedScoped.colorSecundario,
            colorFondoClaro: cachedScoped.colorFondoClaro,
            colorFondoOscuro: cachedScoped.colorFondoOscuro,
            colorSuperficieClaro: cachedScoped.colorSuperficieClaro,
            temaOscuro: cachedScoped.temaOscuro,
          });
        }
      } catch {
        // ignore
      }

      let gymBranding: {
        color_primario?: string | null;
        color_secundario?: string | null;
        color_fondo_claro?: string | null;
        color_fondo_oscuro?: string | null;
        color_superficie_claro?: string | null;
        mobile_color_primario?: string | null;
        mobile_color_secundario?: string | null;
        mobile_color_fondo_claro?: string | null;
        mobile_color_fondo_oscuro?: string | null;
        mobile_tema_oscuro?: boolean | null;
        reglamento_habilitado?: boolean | null;
        reglamento_titulo?: string | null;
        reglamento_contenido?: string | null;
        reglamento_version?: number | null;
        tema_oscuro?: boolean | null;
        sitio_web?: string | null;
        password_temporal_usuarios?: string | null;
        email_bienvenida_activo?: boolean | null;
        email_bienvenida_subject?: string | null;
        email_bienvenida_html?: string | null;
        saas_exento?: boolean | null;
        es_gimnasio_owner?: boolean | null;
        mobile_logo_url?: string | null;
        mobile_home_image_url?: string | null;
      } | null =
        null;
      let emailTemplate: {
        activo?: boolean | null;
        subject?: string | null;
        html?: string | null;
        version?: number | null;
      } | null = null;

      if (gimnasioId) {
        const { data, error } = await supabase
          .from('gimnasios')
          .select('color_primario, color_secundario, color_fondo_claro, color_fondo_oscuro, color_superficie_claro, mobile_color_primario, mobile_color_secundario, mobile_color_fondo_claro, mobile_color_fondo_oscuro, mobile_tema_oscuro, mobile_logo_url, mobile_home_image_url, reglamento_habilitado, reglamento_titulo, reglamento_contenido, reglamento_version, tema_oscuro, sitio_web, password_temporal_usuarios, email_bienvenida_activo, email_bienvenida_subject, email_bienvenida_html, saas_exento, es_gimnasio_owner')
          .eq('id', gimnasioId)
          .maybeSingle();

        if (!error) {
          gymBranding = data as any;
        }

        const { data: templateRows, error: templateErr } = await supabase.rpc('get_gym_email_template', {
          p_gimnasio_id: gimnasioId,
          p_tipo: 'bienvenida',
        });
        if (!templateErr && Array.isArray(templateRows) && templateRows.length > 0) {
          emailTemplate = templateRows[0] as any;
        }
      }

      if (cancelled) return;

      setIsGymExemptFromSaas(!!(gymBranding?.saas_exento || gymBranding?.es_gimnasio_owner));

      setConfigData((prev) => ({
        ...prev,
        nombreGimnasio: sucursalActiva?.nombre || prev.nombreGimnasio,
        direccion: sucursalActiva?.direccion || prev.direccion,
        telefono: sucursalActiva?.telefono || prev.telefono,
        email: sucursalActiva?.email || prev.email,
        ...scopedConfig,
        colorPrimario: gymBranding?.color_primario ?? (scopedConfig as any)?.colorPrimario ?? prev.colorPrimario,
        colorSecundario: gymBranding?.color_secundario ?? (scopedConfig as any)?.colorSecundario ?? prev.colorSecundario,
        colorFondoClaro: gymBranding?.color_fondo_claro ?? (scopedConfig as any)?.colorFondoClaro ?? prev.colorFondoClaro,
        colorFondoOscuro: gymBranding?.color_fondo_oscuro ?? (scopedConfig as any)?.colorFondoOscuro ?? prev.colorFondoOscuro,
        colorSuperficieClaro: gymBranding?.color_superficie_claro ?? (scopedConfig as any)?.colorSuperficieClaro ?? prev.colorSuperficieClaro,
        mobileColorPrimario: gymBranding?.mobile_color_primario ?? (scopedConfig as any)?.mobileColorPrimario ?? prev.mobileColorPrimario,
        mobileColorSecundario: gymBranding?.mobile_color_secundario ?? (scopedConfig as any)?.mobileColorSecundario ?? prev.mobileColorSecundario,
        mobileColorFondoClaro: gymBranding?.mobile_color_fondo_claro ?? (scopedConfig as any)?.mobileColorFondoClaro ?? prev.mobileColorFondoClaro,
        mobileColorFondoOscuro: gymBranding?.mobile_color_fondo_oscuro ?? (scopedConfig as any)?.mobileColorFondoOscuro ?? prev.mobileColorFondoOscuro,
        mobileTemaOscuro: gymBranding?.mobile_tema_oscuro ?? (scopedConfig as any)?.mobileTemaOscuro ?? prev.mobileTemaOscuro,
        reglamentoHabilitado: gymBranding?.reglamento_habilitado ?? (scopedConfig as any)?.reglamentoHabilitado ?? prev.reglamentoHabilitado,
        reglamentoTitulo: gymBranding?.reglamento_titulo ?? (scopedConfig as any)?.reglamentoTitulo ?? prev.reglamentoTitulo,
        reglamentoContenido: gymBranding?.reglamento_contenido ?? (scopedConfig as any)?.reglamentoContenido ?? prev.reglamentoContenido,
        reglamentoVersion: gymBranding?.reglamento_version ?? (scopedConfig as any)?.reglamentoVersion ?? prev.reglamentoVersion,
        temaOscuro: gymBranding?.tema_oscuro ?? (scopedConfig as any)?.temaOscuro ?? prev.temaOscuro,
        sitioWeb: gymBranding?.sitio_web ?? (scopedConfig as any)?.sitioWeb ?? prev.sitioWeb,
        logoUrl: gymBranding?.mobile_logo_url ?? '',
        mobileHomeImageUrl: gymBranding?.mobile_home_image_url ?? '',
        passwordTemporalUsuarios:
          gymBranding?.password_temporal_usuarios ?? (scopedConfig as any)?.passwordTemporalUsuarios ?? prev.passwordTemporalUsuarios,
        emailBienvenida:
          emailTemplate?.activo ??
          gymBranding?.email_bienvenida_activo ??
          (scopedConfig as any)?.emailBienvenida ??
          prev.emailBienvenida,
        emailBienvenidaSubject:
          emailTemplate?.subject ??
          gymBranding?.email_bienvenida_subject ??
          (scopedConfig as any)?.emailBienvenidaSubject ??
          prev.emailBienvenidaSubject,
        emailBienvenidaHtml:
          emailTemplate?.html ??
          gymBranding?.email_bienvenida_html ??
          (scopedConfig as any)?.emailBienvenidaHtml ??
          prev.emailBienvenidaHtml,
        emailBienvenidaVersion:
          Number(emailTemplate?.version || 0) > 0
            ? Number(emailTemplate?.version)
            : (scopedConfig as any)?.emailBienvenidaVersion || prev.emailBienvenidaVersion,
        idioma: 'es',
        datosFacturacion: false,
      }));
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursalActiva?.id, (sucursalActiva as any)?.gimnasio_id, profile?.gimnasioId]);

  useLayoutEffect(() => {
    applyBrandingTheme({
      colorPrimario: configData.colorPrimario,
      colorSecundario: configData.colorSecundario,
      colorFondoClaro: configData.colorFondoClaro,
      colorFondoOscuro: configData.colorFondoOscuro,
      colorSuperficieClaro: configData.colorSuperficieClaro,
      temaOscuro: configData.temaOscuro,
    });
  }, [configData.colorPrimario, configData.colorSecundario, configData.colorFondoClaro, configData.colorFondoOscuro, configData.colorSuperficieClaro, configData.temaOscuro]);

  useEffect(() => {
    if (activeConfigModal !== 'correos-automaticos') return;
    if (emailTestTo.trim()) return;
    const defaultEmail = String(profile?.email || '').trim();
    if (defaultEmail) setEmailTestTo(defaultEmail);
  }, [activeConfigModal, profile?.email, emailTestTo]);

  useEffect(() => {
    // Persist last applied theme so we can apply it immediately on refresh (main.tsx) and on gym switch.
    try {
      const payload = {
        colorPrimario: configData.colorPrimario,
        colorSecundario: configData.colorSecundario,
        colorFondoClaro: configData.colorFondoClaro,
        colorFondoOscuro: configData.colorFondoOscuro,
        colorSuperficieClaro: configData.colorSuperficieClaro,
        mobileColorPrimario: configData.mobileColorPrimario,
        mobileColorSecundario: configData.mobileColorSecundario,
        mobileColorFondoClaro: configData.mobileColorFondoClaro,
        mobileColorFondoOscuro: configData.mobileColorFondoOscuro,
        mobileTemaOscuro: configData.mobileTemaOscuro,
        temaOscuro: configData.temaOscuro,
      };

      localStorage.setItem('gymadmin_branding_last', JSON.stringify(payload));

      const gimnasioId = getCurrentGymId();
      if (gimnasioId) {
        localStorage.setItem(`gymadmin_branding_${gimnasioId}`, JSON.stringify(payload));
      }
    } catch {
      // Best-effort only.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData.colorPrimario, configData.colorSecundario, configData.colorFondoClaro, configData.colorFondoOscuro, configData.colorSuperficieClaro, configData.mobileColorPrimario, configData.mobileColorSecundario, configData.mobileColorFondoClaro, configData.mobileColorFondoOscuro, configData.mobileTemaOscuro, configData.temaOscuro, sucursalActiva?.id]);

  const handleSaveProfile = async () => {
    if (profileSaving || avatarUploading) return;
    setProfileSaving(true);
    try {
      const { error } = await supabase.rpc('actualizar_mi_perfil_admin', {
        p_nombre: profileData.nombre,
        p_telefono: profileData.telefono,
        p_foto_perfil_url: profileData.avatar,
      });
      if (error) throw error;
      setShowProfileModal(false);
    } catch (error: any) {
      console.error('Error guardando perfil:', error);
      alert(error?.message || 'No se pudo guardar el perfil.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError(null);
      setPasswordSuccess(null);

      const currentPassword = passwordData.currentPassword;
      const newPassword = passwordData.newPassword.trim();
      const confirmPassword = passwordData.confirmPassword.trim();

      if (!currentPassword) {
        setPasswordError('Ingresa tu contraseña actual.');
        return;
      }

      if (newPassword.length < 8) {
        setPasswordError('La nueva contraseña debe tener al menos 8 caracteres.');
        return;
      }

      if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
        setPasswordError('La nueva contraseña debe incluir mayúscula, minúscula y número.');
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordError('Las contraseñas no coinciden.');
        return;
      }

      setPasswordSaving(true);

      const { data: currentUserData, error: currentUserError } = await supabase.auth.getUser();
      const currentEmail = currentUserData.user?.email?.trim().toLowerCase();
      if (currentUserError || !currentEmail) {
        throw new Error('No se pudo verificar la sesión actual. Inicia sesión nuevamente.');
      }

      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: currentPassword,
      });
      if (reauthError) {
        throw new Error('La contraseña actual no es correcta.');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw new Error(updateError.message || 'No se pudo actualizar la contraseña.');
      }

      setPasswordSuccess('Contraseña actualizada correctamente.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordError(err?.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file, 'logo');
    }
  };

  const handleMobileHomeImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) {
      alert('No se pudo resolver el gimnasio para subir la portada.');
      return;
    }
    try {
      const optimized = await compressMobileHomeImage(file);
      const path = `${gimnasioId}/home-cover.webp`;
      const { error } = await supabase.storage
        .from('mobile-branding')
        .upload(path, optimized, { contentType: 'image/webp', upsert: true, cacheControl: '3600' });
      if (error) throw error;
      const publicUrl = supabase.storage.from('mobile-branding').getPublicUrl(path).data.publicUrl;
      setConfigData((prev) => ({
        ...prev,
        mobileHomeImageUrl: `${publicUrl}?v=${Date.now()}`,
      }));
    } catch (error: any) {
      console.error('Error subiendo portada móvil:', error);
      alert(error?.message || 'No se pudo subir la portada móvil.');
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void processImageFile(file, 'avatar');
    }
  };

  const processImageFile = async (file: File, type: 'logo' | 'avatar') => {
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 2MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido.');
      return;
    }
    if (type === 'avatar') {
      const gimnasioId = profile?.gimnasioId;
      const adminId = profile?.adminId;
      if (!gimnasioId || !adminId) {
        alert('No se pudo resolver tu perfil para subir la fotografía.');
        return;
      }
      setAvatarUploading(true);
      try {
        const optimized = await compressMobileHomeImage(file);
        const path = `${gimnasioId}/staff/${adminId}/profile.webp`;
        const { error } = await supabase.storage
          .from('mobile-branding')
          .upload(path, optimized, {
            contentType: 'image/webp',
            upsert: true,
            cacheControl: '3600',
          });
        if (error) throw error;
        const publicUrl = supabase.storage.from('mobile-branding').getPublicUrl(path).data.publicUrl;
        setProfileData((current) => ({ ...current, avatar: `${publicUrl}?v=${Date.now()}` }));
      } catch (error: any) {
        console.error('Error subiendo foto de perfil:', error);
        alert(error?.message || 'No se pudo subir la fotografía.');
      } finally {
        setAvatarUploading(false);
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (type === 'logo') {
        setConfigData(prev => ({ ...prev, logoUrl: event.target?.result as string }));
      } else {
        setProfileData(prev => ({ ...prev, avatar: event.target?.result as string }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Drag & Drop handlers para Logo
  const handleLogoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      void processImageFile(files[0], 'logo');
    }
  };

  // Drag & Drop handlers para Avatar
  const handleAvatarDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(true);
  };

  const handleAvatarDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
  };

  const handleAvatarDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      void processImageFile(files[0], 'avatar');
    }
  };

  const isStaff = profile?.role === 'staff';
  const isCoach = isStaff && ['coach_interno', 'coach_externo'].includes(profile?.tipoStaff || '');
  const isInternalCoach = isStaff && profile?.tipoStaff === 'coach_interno';
  const isReceptionist = isStaff && profile?.tipoStaff === 'recepcionista';
  const isNutritionist = isStaff && profile?.tipoStaff === 'nutricionista';
  useEffect(() => {
    if (!isStaff) return;
    if (activeConfigModal && activeConfigModal !== 'centro-ayuda') setActiveConfigModal(null);
    setShowSucursalesModal(false);
    setShowNewSucursalModal(false);
    setShowEditSucursalModal(false);
  }, [isStaff, activeConfigModal]);

  const helpAudience: HelpAudience = isInternalCoach
    ? 'coach_interno'
    : profile?.tipoStaff === 'coach_externo'
      ? 'coach_externo'
      : isReceptionist
        ? 'recepcionista'
        : isNutritionist
          ? 'nutricionista'
          : 'manager';

  const visibleHelpGuides = HELP_GUIDES.filter((guide) => guide.audience.includes(helpAudience));
  const filteredHelpGuides = visibleHelpGuides.filter((guide) => {
    const term = helpSearch.trim().toLocaleLowerCase('es-CL');
    if (!term) return true;
    return [guide.title, guide.description, ...guide.steps, guide.note || '']
      .some((value) => value.toLocaleLowerCase('es-CL').includes(term));
  });

  const visibleConfigurationOptions = isStaff
    ? [{ categoria: 'Ayuda', items: configuracionOpciones[0].items.filter((item) => item.id === 'centro-ayuda') }]
    : configuracionOpciones.map((category) => ({
        ...category,
        items: category.items.filter((item) => item.id !== 'versiones-moviles' || profile?.role === 'platform_admin'),
      }));
  const regularMenuItems = [
    { path: '/', icon: 'ri-dashboard-line', label: 'Dashboard' },
    ...(profile?.role === 'platform_admin'
      ? [
          { path: '/gimnasios', icon: 'ri-building-2-line', label: 'Gimnasios' },
          { path: '/saas-planes', icon: 'ri-price-tag-3-line', label: 'Planes SaaS' },
        ]
      : []),
    ...(profile?.role !== 'platform_admin' && !isStaff
      ? (isGymExemptFromSaas === false
          ? [{ path: '/pagar-servicio', icon: 'ri-wallet-3-line', label: 'Pagar Servicio' }]
          : [])
      : []),
    { path: '/usuarios', icon: 'ri-user-line', label: 'Usuarios' },
    ...(!isStaff ? [{ path: '/inscripciones-pendientes', icon: 'ri-user-received-2-line', label: 'Inscripciones pendientes' }] : []),
    { path: '/presencia-personal', icon: 'ri-team-line', label: 'Personal presente' },
    ...(!isStaff ? [{ path: '/planes', icon: 'ri-vip-crown-line', label: 'Planes' }] : []),
    { path: '/horarios', icon: 'ri-calendar-line', label: 'Horarios y Cupos' },
    { path: '/reservas', icon: 'ri-bookmark-line', label: 'Reservas' },
    ...(!isStaff ? [{ path: '/invitados', icon: 'ri-user-add-line', label: 'Invitados' }] : []),
    ...(!isStaff ? [{ path: '/pagos', icon: 'ri-money-dollar-circle-line', label: 'Pagos' }] : []),
    ...(!isStaff ? [{ path: '/ventas', icon: 'ri-shopping-cart-2-line', label: 'Ventas' }] : []),
    { path: '/rutinas', icon: 'ri-heart-pulse-line', label: 'Rutinas' },
    { path: '/propuestas-ia', icon: 'ri-sparkling-2-line', label: 'Propuestas IA' },
    ...(!isStaff ? [{ path: '/actividades-coaches', icon: 'ri-task-line', label: 'Supervisión Coaches' }] : []),
    { path: '/tutoriales', icon: 'ri-video-line', label: 'Tutoriales' },
    ...(!isStaff ? [{ path: '/notificaciones', icon: 'ri-notification-3-line', label: 'Notificaciones' }] : []),
    { path: '/reportes', icon: 'ri-bar-chart-box-line', label: 'Reportes' },
    { path: '/progreso', icon: 'ri-line-chart-line', label: isStaff ? 'Panel Coach' : 'Progreso' },
    ...(isStaff ? [{ path: '/chat', icon: 'ri-chat-3-line', label: 'Chat' }] : []),
  ];
  const menuItems = isCoach
    ? [
        { path: '/progreso', icon: 'ri-line-chart-line', label: 'Mis Clientes' },
        { path: '/propuestas-ia', icon: 'ri-sparkling-2-line', label: 'Propuestas IA' },
        ...(isInternalCoach
          ? [
              { path: '/rutinas', icon: 'ri-heart-pulse-line', label: 'Rutinas' },
              { path: '/reportes', icon: 'ri-calendar-check-line', label: 'Evaluaciones agendadas' },
            ]
          : []),
        { path: '/horarios', icon: 'ri-calendar-line', label: 'Mi Agenda' },
        { path: '/actividades-coaches', icon: 'ri-task-line', label: profile?.supervisorCoaches ? 'Supervisión Coaches' : 'Mis Actividades' },
        { path: '/chat', icon: 'ri-chat-3-line', label: 'Chat' },
      ]
    : isReceptionist
      ? [
          { path: '/', icon: 'ri-dashboard-line', label: 'Dashboard' },
          { path: '/usuarios', icon: 'ri-user-line', label: 'Usuarios' },
          { path: '/reservas', icon: 'ri-bookmark-line', label: 'Reservas' },
          { path: '/ventas', icon: 'ri-shopping-cart-2-line', label: 'Ventas' },
          { path: '/invitados', icon: 'ri-user-add-line', label: 'Invitados' },
          { path: '/presencia-personal', icon: 'ri-team-line', label: 'Personal presente' },
        ]
      : isNutritionist
        ? [
            { path: '/progreso', icon: 'ri-heart-add-line', label: 'Nutrición y progreso' },
          ]
        : regularMenuItems;

  const getNotificationIcon = (tipo: string) => {
    switch (tipo) {
      case 'pago': return 'ri-money-dollar-circle-fill text-green-500';
      case 'reserva': return 'ri-calendar-check-fill text-blue-500';
      case 'alerta': return 'ri-alarm-warning-fill text-orange-500';
      case 'usuario': return 'ri-user-add-fill text-teal-500';
      case 'vencimiento': return 'ri-time-fill text-red-500';
      default: return 'ri-notification-fill text-gray-500';
    }
  };

  const handleConfigItemClick = (itemId: string) => {
    setActiveConfigModal(itemId);
    setShowConfig(false);
  };

  const isPagoConfigModal = (id: string | null) =>
    id === 'metodos-pago' || id === 'comisiones';

  const loadPagoProviderSecrets = async (
    provider: string,
    mode: 'sandbox' | 'production',
    gimnasioIdParam?: string | null
  ) => {
    const gimnasioId = gimnasioIdParam || getCurrentGymId();
    if (!gimnasioId) return;

    if (provider === 'mercadopago') {
      setMercadoPagoSecrets((prev) => ({ ...prev, loading: true, error: null }));
      const { data, error } = await supabase.functions.invoke('payments-provider-secrets', {
        body: { action: 'get', gimnasio_id: gimnasioId, proveedor: provider, modo: mode },
      });
      setMercadoPagoSecrets((prev) => ({
        ...prev, loading: false, configured: !error && !data?.error && !!data?.configured,
        maskedPublicKey: String(data?.masked_public_key || ''), maskedAccessToken: String(data?.masked_access_token || ''),
        maskedWebhookSecret: String(data?.masked_webhook_secret || ''), webhookUrl: String(data?.webhook_url || ''),
        error: error ? 'No se pudieron cargar las credenciales de Mercado Pago.' : data?.error ? String(data.error) : null,
      }));
      return;
    }
    const setSecrets = provider === 'transbank_oneclick_mall' ? setOneclickProviderSecrets : setPagoProviderSecrets;
    if (!['transbank_webpay_plus', 'transbank_oneclick_mall'].includes(provider)) {
      setSecrets({
        commerceCode: '',
        childCommerceCode: '',
        apiKeySecret: '',
        configured: false,
        maskedCommerceCode: '',
        maskedChildCommerceCode: '',
        maskedApiKeySecret: '',
        loading: false,
        error: null,
      });
      return;
    }

    setSecrets((prev) => ({ ...prev, loading: true, error: null }));
    const { data, error } = await supabase.functions.invoke('payments-provider-secrets', {
      body: {
        action: 'get',
        gimnasio_id: gimnasioId,
        proveedor: provider,
        modo: mode,
      },
    });

    if (error) {
      console.error('Error cargando credenciales de pago:', error);
      setSecrets((prev) => ({
        ...prev,
        loading: false,
        configured: false,
        maskedCommerceCode: '',
        maskedChildCommerceCode: '',
        maskedApiKeySecret: '',
        error: 'No se pudieron cargar las credenciales del proveedor.',
      }));
      return;
    }

    if (data?.error) {
      setSecrets((prev) => ({
        ...prev,
        loading: false,
        configured: false,
        maskedCommerceCode: '',
        maskedChildCommerceCode: '',
        maskedApiKeySecret: '',
        error: String(data.error),
      }));
      return;
    }

    setSecrets((prev) => ({
      ...prev,
      loading: false,
      configured: !!data?.configured,
      maskedCommerceCode: String(data?.masked_commerce_code || ''),
      maskedChildCommerceCode: String(data?.masked_child_commerce_code || ''),
      maskedApiKeySecret: String(data?.masked_api_key_secret || ''),
      error: null,
    }));
    if (provider === 'transbank_webpay_plus' && data?.configured) setEditingWebpayCredentials(false);
  };

  const loadPagoConfig = async () => {
    if (!sucursalActiva?.id) return;
    const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId;
    if (!gimnasioId) return;

    // Prefer sucursal-specific config, fallback to gym default.
    const { data: cfgSucursal } = await supabase
      .from('pago_configuracion')
      .select('*')
      .eq('gimnasio_id', gimnasioId)
      .eq('sucursal_id', sucursalActiva.id)
      .maybeSingle();

    const { data: cfgGym } = cfgSucursal
      ? { data: null as any }
      : await supabase
          .from('pago_configuracion')
          .select('*')
          .eq('gimnasio_id', gimnasioId)
          .is('sucursal_id', null)
          .maybeSingle();

    const cfg = cfgSucursal || cfgGym;
    const selectedProvider = String(cfg?.online_proveedor || configData.onlineProveedor || 'transbank_webpay_plus');
    const selectedMode = (cfg?.online_modo === 'production' ? 'production' : configData.onlineModo === 'production' ? 'production' : 'sandbox') as
      | 'sandbox'
      | 'production';

    if (cfg) {
      setConfigData((prev) => ({
        ...prev,
        efectivo: cfg.efectivo ?? prev.efectivo,
        tarjetaCredito: cfg.tarjeta_credito ?? prev.tarjetaCredito,
        tarjetaDebito: cfg.tarjeta_debito ?? prev.tarjetaDebito,
        transferencia: cfg.transferencia ?? prev.transferencia,
        otroMetodo: cfg.otro ?? prev.otroMetodo,
        comisionTarjeta: cfg.comision_tarjeta ?? prev.comisionTarjeta,
        comisionTransferencia: cfg.comision_transferencia ?? prev.comisionTransferencia,
        datosFacturacion: false,
        onlineActivo: cfg.online_activo ?? prev.onlineActivo,
        onlineProveedor: cfg.online_proveedor ?? prev.onlineProveedor,
        onlineModo: (cfg.online_modo === 'production' ? 'production' : 'sandbox') as 'sandbox' | 'production',
        transbankWebpayActivo: cfg.transbank_webpay_activo ?? (cfg.online_activo && cfg.online_proveedor === 'transbank_webpay_plus'),
        transbankOneclickActivo: cfg.transbank_oneclick_activo ?? false,
        mercadoPagoActivo: cfg.mercadopago_activo ?? false,
        fintocActivo: cfg.fintoc_activo ?? false,
        flowActivo: cfg.flow_activo ?? false,
        posJornadaActiva: cfg.pos_jornada_activa ?? prev.posJornadaActiva,
        posArqueoEfectivo: cfg.efectivo === true && cfg.pos_arqueo_efectivo !== false,
        posEmailCierreActivo: cfg.pos_email_cierre_activo ?? prev.posEmailCierreActivo,
      }));
    }

    await loadPagoProviderSecrets(selectedProvider, selectedMode, gimnasioId);
    await loadPagoProviderSecrets('transbank_oneclick_mall', selectedMode, gimnasioId);
    await loadPagoProviderSecrets('mercadopago', selectedMode, gimnasioId);
  };

  useEffect(() => {
    if (!isPagoConfigModal(activeConfigModal)) return;
    loadPagoConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfigModal, sucursalActiva?.id]);

  useEffect(() => {
    if (activeConfigModal !== 'usuarios-admin') return;
    loadUsuariosAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfigModal, (sucursalActiva as any)?.gimnasio_id, profile?.gimnasioId]);

  const handleSaveConfig = async () => {
    if (!activeConfigModal) return;

    try {
      setSavingConfig(true);

      if (activeConfigModal === 'personalizacion') {
        const gimnasioId = getCurrentGymId();
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar la personalización.');
          return;
        }

        const { error } = await supabase.rpc('set_gym_personalizacion', {
          p_gimnasio_id: gimnasioId,
          p_color_primario: configData.colorPrimario,
          p_color_secundario: configData.colorSecundario,
          p_color_fondo_claro: configData.colorFondoClaro,
          p_color_fondo_oscuro: configData.colorFondoOscuro,
          p_color_superficie_claro: configData.colorSuperficieClaro,
          p_mobile_color_primario: configData.mobileColorPrimario,
          p_mobile_color_secundario: configData.mobileColorSecundario,
          p_mobile_color_fondo_claro: configData.mobileColorFondoClaro,
          p_mobile_color_fondo_oscuro: configData.mobileColorFondoOscuro,
          p_mobile_tema_oscuro: !!configData.mobileTemaOscuro,
          p_tema_oscuro: !!configData.temaOscuro,
        });

        if (error) {
          console.error('Error guardando personalización del gimnasio:', error);
          alert('No se pudo guardar la personalización.');
          return;
        }

        const { error: homeImageError } = await supabase.rpc('set_gym_mobile_home_image', {
          p_gimnasio_id: gimnasioId,
          p_mobile_home_image_url: configData.mobileHomeImageUrl || null,
        });
        if (homeImageError) {
          console.error('Error guardando portada móvil:', homeImageError);
          alert('Los colores se guardaron, pero no se pudo guardar la portada móvil.');
          return;
        }
      }

      if (activeConfigModal === 'reglamento') {
        const gimnasioId = getCurrentGymId();
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar el reglamento.');
          return;
        }

        const nextVersion = Math.max(1, Number(configData.reglamentoVersion) || 1);
        const payload = {
          reglamento_habilitado: !!configData.reglamentoHabilitado,
          reglamento_titulo: String(configData.reglamentoTitulo || '').trim() || 'Reglamento del gimnasio',
          reglamento_contenido: String(configData.reglamentoContenido || '').trim(),
          reglamento_version: nextVersion,
          reglamento_actualizado_at: new Date().toISOString(),
        };

        const { error } = await supabase.from('gimnasios').update(payload).eq('id', gimnasioId);
        if (error) {
          console.error('Error guardando reglamento:', error);
          alert('No se pudo guardar el reglamento.');
          return;
        }
      }

      if (isPagoConfigModal(activeConfigModal)) {
        if (!sucursalActiva?.id) {
          alert('Debes seleccionar una sucursal antes de guardar la configuración de pagos.');
          return;
        }

        const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId;
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar la configuración.');
          return;
        }

        const selectedProvider = String(configData.onlineProveedor || 'transbank_webpay_plus');
        const selectedMode = configData.onlineModo === 'production' ? 'production' : 'sandbox';

        const payload = {
          gimnasio_id: gimnasioId,
          sucursal_id: sucursalActiva.id,
          efectivo: !!configData.efectivo,
          tarjeta_credito: !!configData.tarjetaCredito,
          tarjeta_debito: !!configData.tarjetaDebito,
          transferencia: !!configData.transferencia,
          otro: !!configData.otroMetodo,
          comision_tarjeta: Number(configData.comisionTarjeta) || 0,
          comision_transferencia: Number(configData.comisionTransferencia) || 0,
          solicitar_facturacion: false,
          online_activo: !!configData.onlineActivo,
          online_proveedor: configData.onlineActivo && configData.transbankWebpayActivo ? selectedProvider : null,
          online_modo: selectedMode,
          transbank_webpay_activo: !!configData.onlineActivo && !!configData.transbankWebpayActivo,
          transbank_oneclick_activo: !!configData.onlineActivo && !!configData.transbankOneclickActivo,
          mercadopago_activo: !!configData.onlineActivo && !!configData.mercadoPagoActivo,
          fintoc_activo: !!configData.onlineActivo && !!configData.fintocActivo,
          flow_activo: !!configData.onlineActivo && !!configData.flowActivo,
          pos_jornada_activa: !!configData.posJornadaActiva,
          pos_arqueo_efectivo: !!configData.posJornadaActiva && !!configData.efectivo && !!configData.posArqueoEfectivo,
          pos_email_cierre_activo: !!configData.posJornadaActiva && !!configData.posEmailCierreActivo,
        };

        const { error } = await supabase.from('pago_configuracion').upsert(payload, {
          onConflict: 'gimnasio_id,sucursal_id',
        });

        if (error) {
          console.error('Error guardando pago_configuracion:', error);
          alert('No se pudo guardar la configuración de pagos.');
          return;
        }

        if (configData.onlineActivo && configData.transbankWebpayActivo && selectedProvider === 'transbank_webpay_plus') {
          const commerceCode = pagoProviderSecrets.commerceCode.trim();
          const apiKeySecret = pagoProviderSecrets.apiKeySecret.trim();
          const hasNewCredentials = commerceCode.length > 0 || apiKeySecret.length > 0;

          if (!pagoProviderSecrets.configured && !hasNewCredentials) {
            alert('Para habilitar pagos online debes ingresar código de comercio y API Key Secret de Transbank.');
            return;
          }

          if (hasNewCredentials) {
            const { data: secretData, error: secretError } = await supabase.functions.invoke('payments-provider-secrets', {
              body: {
                action: 'upsert',
                gimnasio_id: gimnasioId,
                proveedor: selectedProvider,
                modo: selectedMode,
                commerce_code: commerceCode || undefined,
                api_key_secret: apiKeySecret || undefined,
              },
            });

            if (secretError) {
              console.error('Error guardando credenciales de pago:', secretError);
              alert('No se pudieron guardar las credenciales del proveedor.');
              return;
            }

            if (secretData?.error) {
              alert(String(secretData.error));
              return;
            }

            setPagoProviderSecrets((prev) => ({
              ...prev,
              commerceCode: '',
              apiKeySecret: '',
              configured: !!secretData?.configured,
              maskedCommerceCode: String(secretData?.masked_commerce_code || ''),
              maskedApiKeySecret: String(secretData?.masked_api_key_secret || ''),
              error: null,
            }));
          } else {
            await loadPagoProviderSecrets(selectedProvider, selectedMode, gimnasioId);
          }
        }

        if (configData.onlineActivo && configData.transbankOneclickActivo) {
          const commerceCode = oneclickProviderSecrets.commerceCode.trim();
          const childCommerceCode = oneclickProviderSecrets.childCommerceCode.trim();
          const apiKeySecret = oneclickProviderSecrets.apiKeySecret.trim();
          const hasNewCredentials = !!(commerceCode || childCommerceCode || apiKeySecret);
          if (hasNewCredentials) {
            const { data: oneclickData, error: oneclickError } = await supabase.functions.invoke('payments-provider-secrets', {
              body: {
                action: 'upsert', gimnasio_id: gimnasioId, proveedor: 'transbank_oneclick_mall', modo: selectedMode,
                commerce_code: commerceCode || undefined,
                child_commerce_code: childCommerceCode || undefined,
                api_key_secret: apiKeySecret || undefined,
              },
            });
            if (oneclickError || oneclickData?.error) {
              alert(String(oneclickData?.error || 'No se pudieron guardar las credenciales Oneclick.'));
              return;
            }
            setOneclickProviderSecrets((prev) => ({
              ...prev, commerceCode: '', childCommerceCode: '', apiKeySecret: '', configured: true,
              maskedCommerceCode: String(oneclickData?.masked_commerce_code || ''),
              maskedChildCommerceCode: String(oneclickData?.masked_child_commerce_code || ''),
              maskedApiKeySecret: String(oneclickData?.masked_api_key_secret || ''), error: null,
            }));
          }

        }

        if (configData.onlineActivo && configData.mercadoPagoActivo) {
          const publicKey = mercadoPagoSecrets.publicKey.trim();
          const accessToken = mercadoPagoSecrets.accessToken.trim();
          const webhookSecret = mercadoPagoSecrets.webhookSecret.trim();
          if (publicKey || accessToken || webhookSecret) {
            const { data: mpData, error: mpError } = await supabase.functions.invoke('payments-provider-secrets', {
              body: { action: 'upsert', gimnasio_id: gimnasioId, proveedor: 'mercadopago', modo: selectedMode,
                public_key: publicKey || undefined, access_token: accessToken || undefined, webhook_secret: webhookSecret || undefined },
            });
            if (mpError || mpData?.error) {
              alert(String(mpData?.error || 'No se pudieron guardar las credenciales de Mercado Pago.'));
              return;
            }
            setMercadoPagoSecrets((prev) => ({ ...prev, publicKey: '', accessToken: '', webhookSecret: '', configured: true,
              maskedPublicKey: String(mpData?.masked_public_key || ''), maskedAccessToken: String(mpData?.masked_access_token || ''),
              maskedWebhookSecret: String(mpData?.masked_webhook_secret || ''), webhookUrl: String(mpData?.webhook_url || ''), error: null }));
          }
        }
      }

      if (activeConfigModal === 'datos-gimnasio') {
        if (sucursalActiva?.id) {
          const { error } = await supabase
            .from('sucursales')
            .update({
              nombre: configData.nombreGimnasio.trim() || sucursalActiva.nombre,
              direccion: configData.direccion.trim() || null,
              telefono: configData.telefono.trim() || null,
              email: configData.email.trim() || null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', sucursalActiva.id);

          if (error) {
            console.error('Error guardando datos de sucursal:', error);
            alert('No se pudieron guardar los datos del gimnasio.');
            return;
          }

          await refreshSucursales();
        }

        const gimnasioId = getCurrentGymId();
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar el sitio web.');
          return;
        }

        const { error: gymError } = await supabase.rpc('set_gym_sitio_web', {
          p_gimnasio_id: gimnasioId,
          p_sitio_web: configData.sitioWeb.trim() || null,
        });

        if (gymError) {
          console.error('Error guardando sitio web del gimnasio:', gymError);
          alert('No se pudo guardar el sitio web del gimnasio.');
          return;
        }

        const { error: logoError } = await supabase.rpc('set_gym_mobile_logo', {
          p_gimnasio_id: gimnasioId,
          p_mobile_logo_url: configData.logoUrl || null,
        });

        if (logoError) {
          console.error('Error guardando logo móvil:', logoError);
          alert('No se pudo guardar el logo del gimnasio.');
          return;
        }
      }

      if (activeConfigModal === 'contrasenas') {
        const gimnasioId = getCurrentGymId();
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar la contraseña temporal.');
          return;
        }

        const { error } = await supabase.rpc('set_gym_password_temporal', {
          p_gimnasio_id: gimnasioId,
          p_password_temporal: configData.passwordTemporalUsuarios.trim() || null,
        });

        if (error) {
          console.error('Error guardando contraseña temporal del gimnasio:', error);
          alert('No se pudo guardar la contraseña temporal.');
          return;
        }
      }

      if (activeConfigModal === 'correos-automaticos') {
        const gimnasioId = getCurrentGymId();
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar la plantilla de bienvenida.');
          return;
        }

        const { error } = await supabase.rpc('set_gym_email_template', {
          p_gimnasio_id: gimnasioId,
          p_tipo: 'bienvenida',
          p_activo: !!configData.emailBienvenida,
          p_subject: configData.emailBienvenidaSubject.trim() || null,
          p_html: configData.emailBienvenidaHtml.trim() || null,
        });

        if (error) {
          console.error('Error guardando plantilla de bienvenida:', error);
          alert('No se pudo guardar la plantilla de bienvenida.');
          return;
        }

        setConfigData((prev) => ({
          ...prev,
          emailBienvenidaVersion: Number(prev.emailBienvenidaVersion || 1) + 1,
        }));
      }

      try {
        localStorage.setItem(
          getConfigStorageKey(),
          JSON.stringify({
            ...configData,
            idioma: 'es',
            datosFacturacion: false,
          })
        );
      } catch {
        // Best-effort local persistence for non-DB settings.
      }

      setActiveConfigModal(null);
    } finally {
      setSavingConfig(false);
    }
  };

  const renderConfigModal = () => {
    if (!activeConfigModal) return null;

    if (activeConfigModal === 'sms-whatsapp' && !ENABLE_SMS_WHATSAPP_SETTINGS) {
      return null;
    }
    if (activeConfigModal === 'registro-actividad' && !ENABLE_REGISTRO_ACTIVIDAD_SETTINGS) {
      return null;
    }
    if (activeConfigModal === 'apis-externas' && !ENABLE_APIS_EXTERNAS_SETTINGS) {
      return null;
    }
    if (activeConfigModal === 'respaldos' && !ENABLE_RESPALDOS_SETTINGS) {
      return null;
    }

    const getModalContent = () => {
      switch (activeConfigModal) {
        case 'datos-gimnasio':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Datos del Gimnasio</h3>
                <p className="text-sm text-gray-500 mt-1">Información básica de tu establecimiento</p>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Gimnasio</label>
                    <input
                      type="text"
                      value={configData.nombreGimnasio}
                      onChange={(e) => setConfigData(prev => ({ ...prev, nombreGimnasio: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                    <input
                      type="text"
                      value={configData.direccion}
                      onChange={(e) => setConfigData(prev => ({ ...prev, direccion: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={configData.telefono}
                        onChange={(e) => setConfigData(prev => ({ ...prev, telefono: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={configData.email}
                        onChange={(e) => setConfigData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sitio Web</label>
                    <input
                      type="url"
                      value={configData.sitioWeb}
                      onChange={(e) => setConfigData(prev => ({ ...prev, sitioWeb: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                    <input
                      type="file"
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      className="hidden"
                    />
                    {configData.logoUrl ? (
                      <div 
                        className={`border-2 rounded-lg p-4 transition-all ${
                          isDraggingLogo 
                            ? 'border-teal-500 bg-teal-50 border-solid' 
                            : 'border-gray-200'
                        }`}
                        onDragOver={handleLogoDragOver}
                        onDragLeave={handleLogoDragLeave}
                        onDrop={handleLogoDrop}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                            <img 
                              src={configData.logoUrl} 
                              alt="Logo del gimnasio" 
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Logo cargado</p>
                            <p className="text-xs text-gray-500 mt-1">Arrastra una nueva imagen o usa los botones</p>
                            <div className="flex gap-2 mt-3">
                              <button 
                                onClick={() => logoInputRef.current?.click()}
                                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs cursor-pointer whitespace-nowrap hover:bg-teal-700 transition-colors"
                              >
                                <i className="ri-upload-2-line mr-1"></i>
                                Cambiar
                              </button>
                              <button 
                                onClick={() => setConfigData(prev => ({ ...prev, logoUrl: '' }))}
                                className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs cursor-pointer whitespace-nowrap hover:bg-red-200 transition-colors"
                              >
                                <i className="ri-delete-bin-line mr-1"></i>
                                Eliminar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
                          isDraggingLogo 
                            ? 'border-teal-500 bg-teal-50 scale-[1.02]' 
                            : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
                        }`}
                        onClick={() => logoInputRef.current?.click()}
                        onDragOver={handleLogoDragOver}
                        onDragLeave={handleLogoDragLeave}
                        onDrop={handleLogoDrop}
                      >
                        <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
                          isDraggingLogo ? 'ring-4 ring-teal-300 scale-105' : 'bg-teal-100'
                        }`}>
                          <i className={`text-3xl transition-colors ${
                            isDraggingLogo ? 'ri-download-2-line text-teal-600' : 'ri-image-add-line text-gray-400'
                          }`}></i>
                        </div>
                        <p className={`text-sm font-medium transition-colors ${
                          isDraggingLogo ? 'text-teal-700' : 'text-gray-600'
                        }`}>
                          {isDraggingLogo ? '¡Suelta la imagen aquí!' : 'Arrastra y suelta tu logo aquí'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">o haz clic para seleccionar</p>
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            logoInputRef.current?.click();
                          }}
                          className="mt-3 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-teal-700 transition-colors"
                        >
                          <i className="ri-folder-open-line mr-2"></i>
                          Seleccionar Archivo
                        </button>
                        <p className="text-xs text-gray-400 mt-3">PNG, JPG, GIF o WebP. Máximo 2MB</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          );

        case 'reglamento':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Reglamento</h3>
                    <p className="text-sm text-gray-500 mt-1">Configura el reglamento que debe aceptar el usuario en la app móvil.</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Habilitar reglamento obligatorio</p>
                      <p className="text-xs text-gray-500 mt-1">Si está activo, el usuario debe aceptar para usar la app móvil.</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!configData.reglamentoHabilitado}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, reglamentoHabilitado: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                      <input
                        type="text"
                        value={configData.reglamentoTitulo}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, reglamentoTitulo: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        placeholder="Reglamento del gimnasio"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Versión</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          value={configData.reglamentoVersion}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, reglamentoVersion: Number(e.target.value) || 1 }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setConfigData((prev) => ({ ...prev, reglamentoVersion: Math.max(1, Number(prev.reglamentoVersion || 1) + 1) }))}
                          className="px-3 py-2.5 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 whitespace-nowrap"
                        >
                          + versión
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contenido del reglamento</label>
                    <textarea
                      rows={12}
                      value={configData.reglamentoContenido}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, reglamentoContenido: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="Escribe aquí las reglas del gimnasio..."
                    />
                  </div>
                </div>
              </div>
            </>
          );

        case 'personalizacion':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Personalización</h3>
                    <p className="text-sm text-gray-500 mt-1">Colores, tema y apariencia de la app</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-6">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-sm font-semibold text-gray-900">Personalización Web</p>
                    <p className="text-xs text-gray-500 mt-1">Colores del panel administrativo.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color Primario</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorPrimario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorPrimario: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorPrimario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorPrimario: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color Secundario</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorSecundario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorSecundario: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorSecundario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorSecundario: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fondo claro</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorFondoClaro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorFondoClaro: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorFondoClaro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorFondoClaro: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fondo oscuro</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorFondoOscuro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorFondoOscuro: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorFondoOscuro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorFondoOscuro: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Superficie clara</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorSuperficieClaro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorSuperficieClaro: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorSuperficieClaro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorSuperficieClaro: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Tema Oscuro</p>
                      <p className="text-xs text-gray-500 mt-1">Habilitar modo oscuro en la interfaz</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={configData.temaOscuro}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, temaOscuro: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </label>

                  <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Vista previa</p>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div className="rounded-lg p-4" style={{ backgroundColor: configData.colorPrimario, color: getContrastText(configData.colorPrimario) }}>
                        <p className="text-sm font-semibold">Primario</p>
                        <p className="text-xs opacity-90">{configData.colorPrimario}</p>
                      </div>
                      <div className="rounded-lg p-4" style={{ backgroundColor: configData.colorSecundario, color: getContrastText(configData.colorSecundario) }}>
                        <p className="text-sm font-semibold">Secundario</p>
                        <p className="text-xs opacity-90">{configData.colorSecundario}</p>
                      </div>
                      <div className="rounded-lg p-4 border border-gray-200" style={{ backgroundColor: configData.colorFondoClaro, color: getContrastText(configData.colorFondoClaro) }}>
                        <p className="text-sm font-semibold">Fondo claro</p>
                        <p className="text-xs opacity-90">{configData.colorFondoClaro}</p>
                      </div>
                      <div className="rounded-lg p-4 border border-gray-200" style={{ backgroundColor: configData.colorSuperficieClaro, color: getContrastText(configData.colorSuperficieClaro) }}>
                        <p className="text-sm font-semibold">Superficie</p>
                        <p className="text-xs opacity-90">{configData.colorSuperficieClaro}</p>
                      </div>
                      <div className="rounded-lg p-4 border border-gray-700" style={{ backgroundColor: configData.colorFondoOscuro, color: getContrastText(configData.colorFondoOscuro) }}>
                        <p className="text-sm font-semibold">Fondo oscuro</p>
                        <p className="text-xs opacity-90">{configData.colorFondoOscuro}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-sm font-semibold text-gray-900">Personalización App Mobile</p>
                    <p className="text-xs text-gray-500 mt-1">Tema que verá el usuario final en la app.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color primario mobile</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.mobileColorPrimario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorPrimario: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.mobileColorPrimario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorPrimario: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color secundario mobile</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.mobileColorSecundario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorSecundario: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.mobileColorSecundario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorSecundario: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fondo claro mobile</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.mobileColorFondoClaro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorFondoClaro: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.mobileColorFondoClaro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorFondoClaro: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fondo oscuro mobile</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.mobileColorFondoOscuro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorFondoOscuro: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.mobileColorFondoOscuro}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, mobileColorFondoOscuro: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Portada del inicio mobile</p>
                        <p className="text-xs text-gray-500 mt-1">Se muestra detrás del saludo del usuario. El portal la optimiza a un máximo de 500 KB.</p>
                      </div>
                      <input ref={mobileHomeImageInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleMobileHomeImageUpload} className="hidden" />
                      <button type="button" onClick={() => mobileHomeImageInputRef.current?.click()} className="shrink-0 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium">
                        <i className="ri-upload-2-line mr-1" />
                        {configData.mobileHomeImageUrl ? 'Cambiar' : 'Subir imagen'}
                      </button>
                    </div>
                    {configData.mobileHomeImageUrl && (
                      <div className="mt-4">
                        <img src={configData.mobileHomeImageUrl} alt="Vista previa de la portada móvil" className="w-full max-h-72 object-cover rounded-xl border border-gray-200" />
                        <button type="button" onClick={() => setConfigData((prev) => ({ ...prev, mobileHomeImageUrl: '' }))} className="mt-3 text-sm font-medium text-red-600">
                          <i className="ri-delete-bin-line mr-1" /> Quitar portada
                        </button>
                      </div>
                    )}
                  </div>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Tema oscuro mobile</p>
                      <p className="text-xs text-gray-500 mt-1">Preferencia por defecto para la app móvil.</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={configData.mobileTemaOscuro}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, mobileTemaOscuro: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </label>
                </div>
              </div>
            </>
          );

        case 'idioma-region':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Idioma y Región</h3>
                    <p className="text-sm text-gray-500 mt-1">Zona horaria, formato de fecha y moneda</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Idioma</label>
                    <select
                      value="es"
                      disabled
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600 cursor-not-allowed"
                    >
                      <option value="es">Español</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-2">El idioma del portal está fijado en Español.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zona horaria</label>
                    <input
                      type="text"
                      value={configData.zonaHoraria}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, zonaHoraria: e.target.value }))}
                      placeholder="America/Santiago"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">Ejemplo: `America/Santiago`, `America/Bogota`, `America/Mexico_City`.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Moneda</label>
                      <select
                        value={configData.moneda}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, moneda: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        <option value="CLP">CLP</option>
                        <option value="ARS">ARS</option>
                        <option value="PEN">PEN</option>
                        <option value="COP">COP</option>
                        <option value="MXN">MXN</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Formato de fecha</label>
                      <select
                        value={configData.formatoFecha}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, formatoFecha: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        <option value="dd/MM/yyyy">dd/MM/yyyy</option>
                        <option value="MM/dd/yyyy">MM/dd/yyyy</option>
                        <option value="yyyy-MM-dd">yyyy-MM-dd</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );

        case 'centro-ayuda':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Centro de ayuda</h3>
                    <p className="text-sm text-gray-500 mt-1">Guías rápidas para {helpAudience === 'manager' ? 'administración' : helpAudience.replace('_', ' ')}</p>
                  </div>
                  <button onClick={() => setActiveConfigModal(null)} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Cerrar">
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-5 max-h-[72vh] overflow-y-auto">
                <div className="relative mb-5">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="search"
                    value={helpSearch}
                    onChange={(event) => setHelpSearch(event.target.value)}
                    placeholder="Buscar una guía o tarea..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>

                {filteredHelpGuides.length === 0 ? (
                  <div className="py-12 text-center">
                    <i className="ri-search-eye-line text-4xl text-gray-300"></i>
                    <p className="mt-3 font-medium text-gray-700">No encontramos esa guía</p>
                    <p className="text-sm text-gray-500 mt-1">Prueba buscando por usuario, pago, rutina o reserva.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredHelpGuides.map((guide) => {
                      const expanded = expandedHelpId === guide.id;
                      return (
                        <div key={guide.id} className={`rounded-xl border transition-colors ${expanded ? 'border-teal-300 bg-teal-50/40' : 'border-gray-200 bg-white'}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedHelpId(expanded ? null : guide.id)}
                            className="w-full p-4 flex items-center gap-4 text-left"
                            aria-expanded={expanded}
                          >
                            <span className="w-11 h-11 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
                              <i className={`${guide.icon} text-xl`}></i>
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-semibold text-gray-900">{guide.title}</span>
                              <span className="block text-sm text-gray-500 mt-0.5">{guide.description}</span>
                            </span>
                            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl text-gray-400`}></i>
                          </button>

                          {expanded && (
                            <div className="px-4 pb-4 sm:pl-[4.75rem]">
                              <ol className="space-y-3">
                                {guide.steps.map((step, index) => (
                                  <li key={`${guide.id}-${index}`} className="flex gap-3 text-sm text-gray-700">
                                    <span className="w-6 h-6 rounded-full bg-teal-700 text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</span>
                                    <span className="pt-0.5">{step}</span>
                                  </li>
                                ))}
                              </ol>
                              {guide.note && (
                                <div className="mt-4 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-2">
                                  <i className="ri-lightbulb-line mt-0.5"></i>
                                  <span>{guide.note}</span>
                                </div>
                              )}
                              {guide.path && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveConfigModal(null);
                                    setHelpSearch('');
                                    navigate(guide.path!);
                                  }}
                                  className="mt-4 px-4 py-2 bg-teal-700 text-white text-sm font-semibold rounded-lg hover:bg-teal-800"
                                >
                                  Ir al módulo <i className="ri-arrow-right-line ml-1"></i>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          );

        case 'alertas-push':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Alertas Push</h3>
                    <p className="text-sm text-gray-500 mt-1">Configurar notificaciones a usuarios</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <ToggleRow
                    title="Reservas"
                    desc="Avisos al reservar, cancelar o cambios de cupos"
                    checked={configData.pushReservas}
                    onChange={(v) => setConfigData((p) => ({ ...p, pushReservas: v }))}
                  />
                  <ToggleRow
                    title="Pagos"
                    desc="Confirmaciones y recordatorios de pago"
                    checked={configData.pushPagos}
                    onChange={(v) => setConfigData((p) => ({ ...p, pushPagos: v }))}
                  />
                  <ToggleRow
                    title="Cancelaciones"
                    desc="Notificar cancelaciones de reservas"
                    checked={configData.pushCancelaciones}
                    onChange={(v) => setConfigData((p) => ({ ...p, pushCancelaciones: v }))}
                  />
                </div>
              </div>
            </>
          );

        case 'correos-automaticos':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Correos Automáticos</h3>
                    <p className="text-sm text-gray-500 mt-1">Plantillas de email y recordatorios</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <ToggleRow
                    title="Email de bienvenida"
                    desc="Enviar al crear usuario con clave temporal"
                    checked={configData.emailBienvenida}
                    onChange={(v) => setConfigData((p) => ({ ...p, emailBienvenida: v }))}
                  />
                  <div className="text-xs text-gray-500">
                    Versión actual de plantilla: v{Number(configData.emailBienvenidaVersion || 1)}
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Asunto bienvenida</label>
                    <input
                      type="text"
                      value={configData.emailBienvenidaSubject}
                      onChange={(e) => setConfigData((p) => ({ ...p, emailBienvenidaSubject: e.target.value }))}
                      placeholder={DEFAULT_WELCOME_SUBJECT}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">HTML bienvenida</label>
                    <textarea
                      rows={10}
                      value={configData.emailBienvenidaHtml}
                      onChange={(e) => setConfigData((p) => ({ ...p, emailBienvenidaHtml: e.target.value }))}
                      placeholder={DEFAULT_WELCOME_HTML}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Variables disponibles: {'{{nombre}}'}, {'{{email}}'}, {'{{clave_temporal}}'}, {'{{gimnasio}}'}.
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setConfigData((p) => ({
                          ...p,
                          emailBienvenidaSubject: DEFAULT_WELCOME_SUBJECT,
                          emailBienvenidaHtml: DEFAULT_WELCOME_HTML,
                        }))
                      }
                      className="mt-3 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Restaurar plantilla por defecto
                    </button>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Vista previa</p>
                    <div
                      className="rounded-lg border border-gray-100 bg-white p-4 text-sm"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(String(configData.emailBienvenidaHtml || DEFAULT_WELCOME_HTML)
                          .replaceAll('{{nombre}}', 'Juan Pérez')
                          .replaceAll('{{email}}', 'juan@email.com')
                          .replaceAll('{{clave_temporal}}', 'Tmp1234!9a')
                          .replaceAll('{{gimnasio}}', configData.nombreGimnasio || 'Tu gimnasio'), {
                            USE_PROFILES: { html: true },
                            FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
                          }),
                      }}
                    />
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                      <input
                        type="email"
                        value={emailTestTo}
                        onChange={(e) => setEmailTestTo(e.target.value)}
                        placeholder="correo@destino.com"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <button
                        type="button"
                        onClick={handleSendWelcomeTestEmail}
                        disabled={emailTestSending}
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                          emailTestSending
                            ? 'bg-teal-400 text-white cursor-not-allowed'
                            : 'bg-teal-600 text-white hover:bg-teal-700'
                        }`}
                      >
                        {emailTestSending ? 'Enviando...' : 'Enviar correo de prueba'}
                      </button>
                    </div>
                  </div>
                  <ToggleRow
                    title="Recordatorios"
                    desc="Recordatorios de vencimiento / asistencia"
                    checked={configData.emailRecordatorios}
                    onChange={(v) => setConfigData((p) => ({ ...p, emailRecordatorios: v }))}
                  />
                  <ToggleRow
                    title="Vencimientos"
                    desc="Avisos de membresías por vencer"
                    checked={configData.emailVencimientos}
                    onChange={(v) => setConfigData((p) => ({ ...p, emailVencimientos: v }))}
                  />
                </div>
              </div>
            </>
          );

        case 'sms-whatsapp':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">SMS y WhatsApp</h3>
                    <p className="text-sm text-gray-500 mt-1">Mensajes de confirmación y avisos</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <ToggleRow
                    title="SMS"
                    desc="Enviar SMS en confirmaciones"
                    checked={configData.smsConfirmaciones}
                    onChange={(v) => setConfigData((p) => ({ ...p, smsConfirmaciones: v }))}
                  />
                  <ToggleRow
                    title="WhatsApp"
                    desc="Enviar mensajes por WhatsApp"
                    checked={configData.whatsappAvisos}
                    onChange={(v) => setConfigData((p) => ({ ...p, whatsappAvisos: v }))}
                  />
                </div>
              </div>
            </>
          );

        case 'comisiones':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Comisiones</h3>
                    <p className="text-sm text-gray-500 mt-1">Configurar tasas y recargos</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Comisión tarjeta</p>
                    <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a pagos con tarjeta</p>
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={configData.comisionTarjeta}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTarjeta: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Comisión transferencia</p>
                    <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a transferencias</p>
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={configData.comisionTransferencia}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTransferencia: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );

        case 'usuarios-admin':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Usuarios Admin</h3>
                    <p className="text-sm text-gray-500 mt-1">Gestionar accesos del personal</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-gray-600">
                        Estos usuarios controlan el portal del gimnasio. La visibilidad es por gimnasio (multi-tenant).
                      </p>
                    </div>
                    <button
                      onClick={() => setInviteAdminOpen((v) => !v)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      Invitar
                    </button>
                  </div>

                  {inviteAdminOpen && (
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={inviteAdminForm.email}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, email: e.target.value }))}
                            placeholder="staff@gimnasio.com"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                          <input
                            type="text"
                            value={inviteAdminForm.nombre}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, nombre: e.target.value }))}
                            placeholder="Nombre y apellido"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                          <input
                            type="tel"
                            value={inviteAdminForm.telefono}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, telefono: e.target.value }))}
                            placeholder="+56 9 1234 5678"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                          <select
                            value={inviteAdminForm.role}
                            onChange={(e) => setInviteAdminForm((p) => ({
                              ...p,
                              role: e.target.value as any,
                              tipo_staff: e.target.value === 'staff' ? p.tipo_staff : 'general',
                            }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                            <option value="gym_admin">Gym Admin</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-2">No existe el rol owner aquí (platform_admin).</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de personal</label>
                          <select
                            value={inviteAdminForm.tipo_staff}
                            disabled={inviteAdminForm.role !== 'staff'}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, tipo_staff: e.target.value as any }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          >
                            <option value="general">Personal general</option>
                            <option value="recepcionista">Recepcionista</option>
                            <option value="nutricionista">Nutricionista</option>
                            <option value="coach_interno">Coach interno</option>
                            <option value="coach_externo">Coach externo</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-2">Disponible para usuarios con rol Staff.</p>
                          {inviteAdminForm.role === 'staff' && inviteAdminForm.tipo_staff === 'coach_interno' && <label className="mt-3 flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={inviteAdminForm.es_supervisor_coaches} onChange={(e) => setInviteAdminForm((p) => ({ ...p, es_supervisor_coaches: e.target.checked }))} className="w-4 h-4" />Coach administrador / supervisor</label>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Sucursal (opcional)</label>
                          <select
                            value={inviteAdminForm.sucursal_id}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, sucursal_id: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="">Todas</option>
                            {(sucursalesDB || []).map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.nombre}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => setInviteAdminOpen(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors cursor-pointer text-sm"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleInviteAdmin}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer text-sm"
                        >
                          Enviar invitación
                        </button>
                      </div>
                    </div>
                  )}

                  {usuariosAdminError && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                      {usuariosAdminError}
                    </div>
                  )}

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">Personal</p>
                      <button
                        onClick={loadUsuariosAdmin}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        Recargar
                      </button>
                    </div>

                    {usuariosAdminLoading ? (
                      <div className="p-4 text-sm text-gray-600">Cargando...</div>
                    ) : usuariosAdmin.length === 0 ? (
                      <div className="p-4 text-sm text-gray-600">Aún no hay usuarios admin para este gimnasio.</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {usuariosAdmin.map((ua) => (
                          <div key={ua.id} className="p-5">
                            {editingAdminId === ua.id ? (
                              <div className="space-y-4">
                                {ua.role !== 'platform_admin' && (
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-teal-200 bg-teal-50/60 p-4">
                                    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full bg-teal-100 flex items-center justify-center">
                                      {editingAdminForm.foto_perfil_url ? (
                                        <img src={editingAdminForm.foto_perfil_url} alt={`Foto de ${editingAdminForm.nombre}`} className="h-full w-full object-cover" />
                                      ) : (
                                        <span className="text-3xl font-bold text-teal-700">{editingAdminForm.nombre.trim().charAt(0).toUpperCase() || 'P'}</span>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-semibold text-gray-900">Fotografía de perfil del personal</p>
                                      <p className="mt-1 text-xs text-gray-600">Es independiente de la captura biométrica. Queda disponible para identificar al integrante en las funciones del portal y, cuando corresponda, en la app móvil.</p>
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        <label className={`cursor-pointer rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 ${editingAdminPhotoUploading ? 'pointer-events-none opacity-60' : ''}`}>
                                          <i className="ri-camera-line mr-2" />
                                          {editingAdminPhotoUploading ? 'Procesando…' : editingAdminForm.foto_perfil_url ? 'Cambiar foto' : 'Tomar o subir foto'}
                                          <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            capture="user"
                                            className="hidden"
                                            onChange={(event) => void handleEditingAdminPhoto(ua, event.target.files?.[0])}
                                          />
                                        </label>
                                        {editingAdminForm.foto_perfil_url && (
                                          <button type="button" onClick={() => setEditingAdminForm((current) => ({ ...current, foto_perfil_url: '' }))} className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                                            Quitar foto
                                          </button>
                                        )}
                                      </div>
                                      <p className="mt-2 text-xs text-gray-500">La imagen se convierte a WEBP y se comprime a un máximo de 500 KB.</p>
                                    </div>
                                  </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <label className="text-sm font-medium text-gray-700">Nombre<input value={editingAdminForm.nombre} onChange={(e) => setEditingAdminForm(p => ({ ...p, nombre: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg" /></label>
                                  <label className="text-sm font-medium text-gray-700">Correo electrónico<input type="email" value={editingAdminForm.email} onChange={(e) => setEditingAdminForm(p => ({ ...p, email: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg" /></label>
                                  <label className="text-sm font-medium text-gray-700 md:col-span-2">Teléfono de contacto<input type="tel" value={editingAdminForm.telefono} onChange={(e) => setEditingAdminForm(p => ({ ...p, telefono: e.target.value }))} placeholder="+56 9 1234 5678" className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg" /></label>
                                  <label className="text-sm font-medium text-gray-700">Rol<select value={editingAdminForm.role} disabled={ua.role === 'platform_admin'} onChange={(e) => setEditingAdminForm(p => ({ ...p, role: e.target.value, tipo_staff: e.target.value === 'staff' ? p.tipo_staff : 'general' }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg"><option value="staff">Staff</option><option value="admin">Admin</option><option value="gym_admin">Gym Admin</option></select></label>
                                  <label className="text-sm font-medium text-gray-700">Tipo de personal<select value={editingAdminForm.tipo_staff} disabled={editingAdminForm.role !== 'staff'} onChange={(e) => setEditingAdminForm(p => ({ ...p, tipo_staff: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg disabled:bg-gray-100"><option value="general">Personal general</option><option value="recepcionista">Recepcionista</option><option value="nutricionista">Nutricionista</option><option value="coach_interno">Coach interno</option><option value="coach_externo">Coach externo</option></select></label>
                                  <label className="md:col-span-2 flex items-center justify-between gap-4 p-3 border border-gray-300 rounded-lg">
                                    <span><span className="block text-sm font-medium text-gray-700">Supervisor de coaches</span><span className="block text-xs text-gray-500 mt-1">Puede asignar y revisar actividades de otros coaches, sin permisos administrativos.</span></span>
                                    <input type="checkbox" checked={editingAdminForm.es_supervisor_coaches} disabled={editingAdminForm.role !== 'staff' || editingAdminForm.tipo_staff !== 'coach_interno'} onChange={(e) => setEditingAdminForm(p => ({ ...p, es_supervisor_coaches: e.target.checked }))} className="w-5 h-5" />
                                  </label>
                                  <label className="text-sm font-medium text-gray-700 md:col-span-2">Sucursal<select value={editingAdminForm.sucursal_id} onChange={(e) => setEditingAdminForm(p => ({ ...p, sucursal_id: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg"><option value="">Todas las sucursales</option>{(sucursalesDB || []).map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}</select></label>
                                </div>
                                {editingAdminForm.role === 'staff' && editingAdminForm.tipo_staff === 'coach_interno' && (
                                  <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-4">
                                    <label className="flex items-center justify-between gap-4">
                                      <span><span className="block text-sm font-semibold text-gray-900">Clientes externos patrocinados</span><span className="block text-xs text-gray-600 mt-1">El coach podrá generar invitaciones QR. Cada alta requerirá aprobación administrativa.</span></span>
                                      <input type="checkbox" checked={editingAdminForm.clientes_externos_habilitados} onChange={(e) => setEditingAdminForm(p => ({ ...p, clientes_externos_habilitados: e.target.checked }))} className="h-5 w-5" />
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <label className="text-sm font-medium text-gray-700">Modalidad<select value={editingAdminForm.clientes_externos_modalidad} onChange={(e) => setEditingAdminForm(p => ({ ...p, clientes_externos_modalidad: e.target.value }))} className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5"><option value="sin_cobro">Autorizado sin cobro</option><option value="coach_tarifa">Tarifa asumida por el coach</option></select></label>
                                      <NumberField label="Máximo de clientes activos" value={editingAdminForm.clientes_externos_max_activos} min={1} max={500} onChange={(value) => setEditingAdminForm(p => ({ ...p, clientes_externos_max_activos: value }))} />
                                      <NumberField label="Máximo por bloque" value={editingAdminForm.clientes_externos_max_por_bloque} min={1} max={100} onChange={(value) => setEditingAdminForm(p => ({ ...p, clientes_externos_max_por_bloque: value }))} />
                                      <NumberField label="Ingreso permitido antes (min)" value={editingAdminForm.clientes_externos_minutos_antes} min={0} max={180} onChange={(value) => setEditingAdminForm(p => ({ ...p, clientes_externos_minutos_antes: value }))} />
                                      <NumberField label="Salida permitida después (min)" value={editingAdminForm.clientes_externos_minutos_despues} min={0} max={180} onChange={(value) => setEditingAdminForm(p => ({ ...p, clientes_externos_minutos_despues: value }))} />
                                      <NumberField label="Vigencia de presencia del coach (horas)" value={editingAdminForm.clientes_externos_presencia_max_horas} min={1} max={24} onChange={(value) => setEditingAdminForm(p => ({ ...p, clientes_externos_presencia_max_horas: value }))} />
                                    </div>
                                    <p className="text-xs text-gray-600">El acceso exigirá una reserva vigente y que el último movimiento autorizado del coach indique que está dentro de la sede. Todos los intentos quedarán registrados.</p>
                                  </section>
                                )}
                                <div className="flex justify-end gap-2"><button onClick={() => setEditingAdminId(null)} className="px-4 py-2 border rounded-lg">Cancelar</button><button disabled={editingAdminPhotoUploading} onClick={() => void saveEditUsuarioAdmin(ua)} className="px-4 py-2 bg-teal-700 text-white rounded-lg disabled:opacity-60">Guardar información</button></div>
                              </div>
                            ) : (
                              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-gray-900">{ua.nombre}</p><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{ua.role}</span>{ua.role === 'staff' && ua.tipo_staff !== 'general' && <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">{{ coach_externo: 'Coach externo', coach_interno: 'Coach interno', recepcionista: 'Recepcionista', nutricionista: 'Nutricionista' }[ua.tipo_staff] || 'Personal'}</span>}{ua.es_supervisor_coaches && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Coach administrador</span>}{ua.tipo_staff === 'coach_interno' && ua.clientes_externos_habilitados && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Patrocinios habilitados</span>}{!ua.activo && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">Inactivo</span>}</div>
                                  <p className="text-sm text-gray-600 mt-1 break-all">{ua.email}</p><p className="text-sm text-gray-600 mt-1"><i className="ri-phone-line mr-1.5" />{ua.telefono || 'Teléfono no registrado'}</p><p className="text-xs text-gray-500 mt-1">Sucursal: {ua.sucursal_id ? (sucursalesDB || []).find(s => s.id === ua.sucursal_id)?.nombre || 'Asignada' : 'Todas'}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap lg:justify-end">{ua.role === 'staff' && <StaffFaceCaptureButton adminId={ua.id} name={ua.nombre} />}<button onClick={() => void resetUsuarioAdminPassword(ua)} disabled={ua.role === 'platform_admin' || resettingAdminAuthId === ua.auth_id} className="px-3 py-2 border border-amber-300 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-50 disabled:opacity-40"><i className="ri-key-2-line mr-1.5" />{resettingAdminAuthId === ua.auth_id ? 'Generando…' : 'Clave temporal'}</button><button onClick={() => openEditUsuarioAdmin(ua)} disabled={ua.role === 'platform_admin'} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40"><i className="ri-edit-line mr-1.5" />Editar</button><button onClick={() => handleUpdateUsuarioAdmin(ua.auth_id, { activo: !ua.activo })} className={`px-3 py-2 rounded-lg text-sm font-medium ${ua.activo ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-teal-600 hover:bg-teal-700 text-white'}`}>{ua.activo ? 'Desactivar' : 'Activar'}</button></div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          );

        case 'contrasenas':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Contraseñas</h3>
                    <p className="text-sm text-gray-500 mt-1">Políticas de seguridad</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitud mínima</label>
                    <input
                      type="number"
                      min={6}
                      value={configData.passwordMinLength}
                      onChange={(e) => setConfigData((p) => ({ ...p, passwordMinLength: Number(e.target.value) }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">Esto es una preferencia del portal; en Supabase Auth se configura por separado.</p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña temporal (usuarios nuevos)</label>
                    <input
                      type="text"
                      value={configData.passwordTemporalUsuarios}
                      onChange={(e) => setConfigData((p) => ({ ...p, passwordTemporalUsuarios: e.target.value }))}
                      placeholder="Ej: Mowe2026!"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Si la defines, los usuarios nuevos se crean con esta clave y luego la cambian en la app móvil. Si la dejas vacía, se mantiene activación por link.
                    </p>
                  </div>
                </div>
              </div>
            </>
          );

        case 'registro-actividad':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Registro de Actividad</h3>
                    <p className="text-sm text-gray-500 mt-1">Historial de acciones del sistema</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Pendiente</p>
                  <p className="text-sm text-gray-600 mt-1">
                    No existe aún una tabla de auditoría. Si la necesitas, la implementamos con triggers/RPC y se muestra aquí.
                  </p>
                </div>
              </div>
            </>
          );

        case 'control-acceso':
          return <AccessControlSettings gimnasioId={getCurrentGymId()} sucursales={sucursalesDB} />;

        case 'modelo-operativo':
          return <OperationalModelSettings gimnasioId={getCurrentGymId()} sucursales={sucursalesDB} onClose={() => setActiveConfigModal(null)} />;

        case 'flex-pass':
          return <FlexPassSettings gimnasioId={getCurrentGymId()} sucursales={sucursalesDB} onClose={() => setActiveConfigModal(null)} />;

        case 'planes-coaches':
          return <CoachPlansSettings gimnasioId={getCurrentGymId()} sucursales={sucursalesDB} onClose={() => setActiveConfigModal(null)} />;

        case 'invitados':
          return <GuestSettings gimnasioId={getCurrentGymId()} onClose={() => setActiveConfigModal(null)} />;

        case 'versiones-moviles':
          return <MobileVersionsSettings onClose={() => setActiveConfigModal(null)} />;

        case 'apis-externas':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">APIs Externas</h3>
                    <p className="text-sm text-gray-500 mt-1">Conexiones con otros servicios</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                    <input
                      type="text"
                      value={configData.apiKey}
                      onChange={(e) => setConfigData((p) => ({ ...p, apiKey: e.target.value }))}
                      placeholder="(opcional)"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">Recomendación: mover esto a Secrets/Edge Functions si se usa en producción.</p>
                  </div>
                </div>
              </div>
            </>
          );

        case 'respaldos':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Respaldos</h3>
                    <p className="text-sm text-gray-500 mt-1">Backup automático de datos</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <ToggleRow
                    title="Respaldo automático"
                    desc="Habilitar backups programados"
                    checked={configData.backupAutomatico}
                    onChange={(v) => setConfigData((p) => ({ ...p, backupAutomatico: v }))}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frecuencia</label>
                    <select
                      value={configData.backupFrecuencia}
                      onChange={(e) => setConfigData((p) => ({ ...p, backupFrecuencia: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    >
                      <option value="diario">Diario</option>
                      <option value="semanal">Semanal</option>
                      <option value="mensual">Mensual</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          );

        case 'metodos-pago':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Métodos de Pago</h3>
                    <p className="text-sm text-gray-500 mt-1">Configura qué medios de pago aceptas y sus comisiones</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-6">
                  <div className="p-4 bg-teal-50 border border-teal-100 rounded-lg">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                      <div>
                        <p className="text-sm font-medium text-teal-900">Nota</p>
                        <p className="text-sm text-teal-800 mt-1">
                          Esta configuración define medios aceptados y también permite guardar credenciales de checkout online (Transbank)
                          sin usar SQL manual.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Pagos online</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Habilita checkout para que el cliente pague desde la app (por ahora: Transbank).
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={configData.onlineActivo}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setConfigData((prev) => ({ ...prev, onlineActivo: enabled }));
                          if (enabled) {
                            loadPagoProviderSecrets(
                              configData.onlineProveedor || 'transbank_webpay_plus',
                              configData.onlineModo === 'production' ? 'production' : 'sandbox',
                              getCurrentGymId()
                            );
                          }
                        }}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer mt-1"
                      />
                    </div>

                    {configData.onlineActivo && (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Proveedor</label>
                          <select
                            value={configData.onlineProveedor}
                            onChange={(e) => {
                              const nextProvider = e.target.value;
                              setConfigData((prev) => ({ ...prev, onlineProveedor: nextProvider }));
                              setPagoProviderSecrets((prev) => ({
                                ...prev,
                                commerceCode: '',
                                apiKeySecret: '',
                              }));
                              setOneclickProviderSecrets((prev) => ({
                                ...prev, commerceCode: '', childCommerceCode: '', apiKeySecret: '',
                              }));
                              loadPagoProviderSecrets(
                                nextProvider,
                                configData.onlineModo === 'production' ? 'production' : 'sandbox',
                                getCurrentGymId()
                              );
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="transbank_webpay_plus">Transbank Webpay Plus</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Modo</label>
                          <select
                            value={configData.onlineModo}
                            onChange={(e) => {
                              const nextMode = e.target.value === 'production' ? 'production' : 'sandbox';
                              setConfigData((prev) => ({
                                ...prev,
                                onlineModo: nextMode,
                              }));
                              setPagoProviderSecrets((prev) => ({
                                ...prev,
                                commerceCode: '',
                                apiKeySecret: '',
                              }));
                              loadPagoProviderSecrets(
                                configData.onlineProveedor || 'transbank_webpay_plus',
                                nextMode,
                                getCurrentGymId()
                              );
                              loadPagoProviderSecrets('transbank_oneclick_mall', nextMode, getCurrentGymId());
                              loadPagoProviderSecrets('mercadopago', nextMode, getCurrentGymId());
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="sandbox">Sandbox</option>
                            <option value="production">Producción</option>
                          </select>
                        </div>

                        {configData.onlineProveedor === 'transbank_webpay_plus' && (
                          <div className={`sm:col-span-2 p-3 border rounded-lg ${configData.transbankWebpayActivo ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-start justify-between gap-4 mb-3">
                              <div><p className="text-xs font-semibold text-gray-900">Transbank Webpay Plus</p><p className="text-xs text-gray-500 mt-1">Pagos únicos desde la app.</p></div>
                              <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                <span>{configData.transbankWebpayActivo ? 'Activo' : 'Inactivo'}</span>
                                <input type="checkbox" checked={configData.transbankWebpayActivo}
                                  onChange={(e) => setConfigData((prev) => ({ ...prev, transbankWebpayActivo: e.target.checked }))}
                                  className="w-4 h-4 text-red-600 rounded" />
                              </label>
                            </div>

                            {(!pagoProviderSecrets.configured || editingWebpayCredentials) && (
                            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${configData.transbankWebpayActivo ? '' : 'opacity-45 pointer-events-none'}`}>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">Código de comercio</label>
                                <input
                                  type="text"
                                  value={pagoProviderSecrets.commerceCode}
                                  onChange={(e) =>
                                    setPagoProviderSecrets((prev) => ({
                                      ...prev,
                                      commerceCode: e.target.value,
                                      error: null,
                                    }))
                                  }
                                  placeholder="Ej: 597055555532"
                                  autoComplete="off"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">API Key Secret</label>
                                <input
                                  type="password"
                                  value={pagoProviderSecrets.apiKeySecret}
                                  onChange={(e) =>
                                    setPagoProviderSecrets((prev) => ({
                                      ...prev,
                                      apiKeySecret: e.target.value,
                                      error: null,
                                    }))
                                  }
                                  placeholder="Ingresa nueva API Key Secret"
                                  autoComplete="new-password"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                              </div>
                            </div>
                            )}

                            <div className="mt-3 space-y-1">
                              {pagoProviderSecrets.loading ? (
                                <p className="text-xs text-gray-500">Cargando estado de credenciales...</p>
                              ) : pagoProviderSecrets.configured ? (
                                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                  <p className="text-xs font-semibold text-emerald-700">
                                    Credenciales guardadas · comercio `{pagoProviderSecrets.maskedCommerceCode || '***'}` · secret `{pagoProviderSecrets.maskedApiKeySecret || '***'}`
                                  </p>
                                  <button type="button" onClick={() => setEditingWebpayCredentials((value) => !value)} className="text-xs font-semibold text-emerald-800 underline">
                                    {editingWebpayCredentials ? 'Cancelar cambio' : 'Cambiar credenciales'}
                                  </button>
                                </div>
                              ) : (
                                <p className="text-xs text-amber-700">
                                  Aún no hay credenciales guardadas para este modo.
                                </p>
                              )}

                              {pagoProviderSecrets.error && (
                                <p className="text-xs text-red-600">{pagoProviderSecrets.error}</p>
                              )}

                              {editingWebpayCredentials && <p className="text-xs text-gray-500">Solo completa los valores que quieras reemplazar. El secreto guardado nunca se muestra completo.</p>}
                              <button type="button" onClick={() => void testPaymentConnection('transbank_webpay_plus')}
                                disabled={paymentConnectionTest.transbank_webpay_plus?.loading || !pagoProviderSecrets.configured}
                                className="mt-2 px-3 py-2 rounded-lg border border-teal-600 text-teal-700 font-semibold disabled:opacity-50">
                                {paymentConnectionTest.transbank_webpay_plus?.loading ? 'Probando…' : 'Probar conexión'}
                              </button>
                              {paymentConnectionTest.transbank_webpay_plus?.message && <p className={`mt-1 ${paymentConnectionTest.transbank_webpay_plus.ok ? 'text-emerald-700' : 'text-red-600'}`}>{paymentConnectionTest.transbank_webpay_plus.message}</p>}
                            </div>
                          </div>
                        )}

                        <div className={`sm:col-span-2 p-3 border rounded-lg ${configData.mercadoPagoActivo ? 'border-sky-200 bg-sky-50/50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div><p className="text-xs font-semibold text-gray-900">Mercado Pago — mensualidades recurrentes</p><p className="text-xs text-gray-500 mt-1">La Public Key tokeniza la tarjeta en el navegador. El Access Token se usa solo en el backend.</p></div>
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                              <span>{configData.mercadoPagoActivo ? 'Activo' : 'Inactivo'}</span>
                              <input type="checkbox" checked={configData.mercadoPagoActivo}
                                onChange={(e) => setConfigData((prev) => ({ ...prev, mercadoPagoActivo: e.target.checked }))}
                                className="w-4 h-4 text-sky-600 rounded" />
                            </label>
                          </div>
                          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${configData.mercadoPagoActivo ? '' : 'opacity-45 pointer-events-none'}`}>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">Public Key</label>
                              <input type="text" value={mercadoPagoSecrets.publicKey}
                                onChange={(e) => setMercadoPagoSecrets((p) => ({ ...p, publicKey: e.target.value, error: null }))}
                                placeholder="APP_USR-... o TEST-..." autoComplete="off"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">Access Token</label>
                              <input type="password" value={mercadoPagoSecrets.accessToken}
                                onChange={(e) => setMercadoPagoSecrets((p) => ({ ...p, accessToken: e.target.value, error: null }))}
                                placeholder="Credencial privada" autoComplete="new-password"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">Secret del webhook</label>
                              <input type="password" value={mercadoPagoSecrets.webhookSecret}
                                onChange={(e) => setMercadoPagoSecrets((p) => ({ ...p, webhookSecret: e.target.value, error: null }))}
                                placeholder="Clave secreta de Webhooks" autoComplete="new-password"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm" />
                            </div>
                          </div>
                          <div className="mt-3 text-xs">
                            {mercadoPagoSecrets.loading ? <span className="text-gray-500">Cargando credenciales Mercado Pago...</span>
                              : mercadoPagoSecrets.configured ? <span className="text-emerald-700">Configuradas: Public Key `{mercadoPagoSecrets.maskedPublicKey || '***'}`, Access Token `{mercadoPagoSecrets.maskedAccessToken || '***'}` y webhook `{mercadoPagoSecrets.maskedWebhookSecret || 'sin configurar'}`</span>
                              : <span className="text-amber-700">Aún no hay credenciales Mercado Pago guardadas para este modo.</span>}
                            {mercadoPagoSecrets.error && <p className="text-red-600 mt-1">{mercadoPagoSecrets.error}</p>}
                            <p className="text-gray-500 mt-1">Si dejas un campo vacío se mantiene su valor actual. El Access Token nunca vuelve a mostrarse completo.</p>
                            {mercadoPagoSecrets.webhookUrl && <div className="mt-2 rounded-lg border border-sky-200 bg-white p-2"><p className="font-medium text-gray-700">URL del webhook para este gimnasio y modo</p><code className="block mt-1 break-all select-all text-[11px]">{mercadoPagoSecrets.webhookUrl}</code></div>}
                            <button type="button" onClick={() => void testPaymentConnection('mercadopago')}
                              disabled={paymentConnectionTest.mercadopago?.loading || !mercadoPagoSecrets.configured}
                              className="mt-2 px-3 py-2 rounded-lg border border-sky-600 text-sky-700 font-semibold disabled:opacity-50">
                              {paymentConnectionTest.mercadopago?.loading ? 'Probando…' : 'Probar conexión'}
                            </button>
                            {paymentConnectionTest.mercadopago?.message && <p className={`mt-1 ${paymentConnectionTest.mercadopago.ok ? 'text-emerald-700' : 'text-red-600'}`}>{paymentConnectionTest.mercadopago.message}</p>}
                          </div>
                        </div>

                        <div className="sm:col-span-2 p-3 border rounded-lg space-y-3">
                          <label className="flex justify-between gap-3 font-semibold text-sm">Fintoc — pagos recurrentes<input type="checkbox" checked={configData.fintocActivo} onChange={e=>setConfigData(prev=>({...prev,fintocActivo:e.target.checked}))}/></label>
                          <FintocSettings gym={getCurrentGymId()} mode={configData.onlineModo}/>
                        </div>
                        <div className={`sm:col-span-2 p-3 border rounded-lg space-y-3 ${configData.flowActivo ? 'border-violet-200 bg-violet-50/40' : 'border-gray-200 bg-gray-50'}`}>
                          <label className="flex justify-between gap-3 font-semibold text-sm">Flow — tarjetas recurrentes<input type="checkbox" checked={configData.flowActivo} onChange={e=>setConfigData(prev=>({...prev,flowActivo:e.target.checked}))}/></label>
                          <div className={configData.flowActivo ? '' : 'opacity-45 pointer-events-none'}><FlowSettings gym={getCurrentGymId()} mode={configData.onlineModo}/></div>
                        </div>
                        <div className={`sm:col-span-2 p-3 border rounded-lg ${configData.transbankOneclickActivo ? 'border-indigo-200 bg-indigo-50/40' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div><p className="text-xs font-semibold text-gray-900">Transbank Oneclick Mall — mensualidades recurrentes</p><p className="text-xs text-gray-500 mt-1">Se usa solamente en planes marcados como recurrentes.</p></div>
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                              <span>{configData.transbankOneclickActivo ? 'Activo' : 'Inactivo'}</span>
                              <input type="checkbox" checked={configData.transbankOneclickActivo}
                                onChange={(e) => setConfigData((prev) => ({ ...prev, transbankOneclickActivo: e.target.checked }))}
                                className="w-4 h-4 text-indigo-600 rounded" />
                            </label>
                          </div>
                          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${configData.transbankOneclickActivo ? '' : 'opacity-45 pointer-events-none'}`}>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">Código Mall</label>
                              <input type="text" value={oneclickProviderSecrets.commerceCode}
                                onChange={(e) => setOneclickProviderSecrets((p) => ({ ...p, commerceCode: e.target.value, error: null }))}
                                placeholder="Código comercio padre" autoComplete="off"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">Código comercio hijo</label>
                              <input type="text" value={oneclickProviderSecrets.childCommerceCode}
                                onChange={(e) => setOneclickProviderSecrets((p) => ({ ...p, childCommerceCode: e.target.value, error: null }))}
                                placeholder="Código para los cobros" autoComplete="off"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">API Key Secret</label>
                              <input type="password" value={oneclickProviderSecrets.apiKeySecret}
                                onChange={(e) => setOneclickProviderSecrets((p) => ({ ...p, apiKeySecret: e.target.value, error: null }))}
                                placeholder="Ingresa nueva API Key" autoComplete="new-password"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
                            </div>
                          </div>
                          <div className="mt-3 text-xs">
                            {oneclickProviderSecrets.loading ? <span className="text-gray-500">Cargando credenciales Oneclick...</span>
                              : oneclickProviderSecrets.configured ? <span className="text-emerald-700">Configuradas: Mall `{oneclickProviderSecrets.maskedCommerceCode || '***'}`, hijo `{oneclickProviderSecrets.maskedChildCommerceCode || '***'}` y secret `{oneclickProviderSecrets.maskedApiKeySecret || '***'}`</span>
                              : <span className="text-amber-700">Aún no hay credenciales Oneclick guardadas para este modo.</span>}
                            {oneclickProviderSecrets.error && <p className="text-red-600 mt-1">{oneclickProviderSecrets.error}</p>}
                            <button type="button" onClick={() => void testPaymentConnection('transbank_oneclick_mall')}
                              disabled={paymentConnectionTest.transbank_oneclick_mall?.loading || !oneclickProviderSecrets.configured}
                              className="mt-2 px-3 py-2 rounded-lg border border-indigo-600 text-indigo-700 font-semibold disabled:opacity-50">
                              {paymentConnectionTest.transbank_oneclick_mall?.loading ? 'Probando…' : 'Probar conexión'}
                            </button>
                            {paymentConnectionTest.transbank_oneclick_mall?.message && <p className={`mt-1 ${paymentConnectionTest.transbank_oneclick_mall.ok ? 'text-emerald-700' : 'text-red-600'}`}>{paymentConnectionTest.transbank_oneclick_mall.message}</p>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/60">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0"><i className="ri-store-2-line text-emerald-700 text-xl"></i></div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">POS · Jornada de ventas</p>
                        <p className="text-xs text-gray-500 mt-1">Activa únicamente las funciones que usará esta sucursal.</p>
                        <div className="mt-3 space-y-2">
                          {[
                            {key:'posJornadaActiva',title:'Control de jornada',detail:'Exige abrir y cerrar una jornada para agrupar las ventas.'},
                            {key:'posArqueoEfectivo',title:'Arqueo de efectivo',detail:'Solicita contar el efectivo al cerrar.',disabled:!configData.posJornadaActiva||!configData.efectivo},
                            {key:'posEmailCierreActivo',title:'Enviar resumen por correo',detail:'Envía el cierre a Administración Mowe y Laetitia SpA.',disabled:!configData.posJornadaActiva||getCurrentGymId()!=='2ccab5f8-70e7-410d-834a-c170bc4cea7c'},
                          ].map(item=><label key={item.key} className={`flex items-center justify-between gap-4 rounded-lg border bg-white p-3 ${item.disabled?'opacity-50':'cursor-pointer'}`}>
                            <span><span className="block text-sm font-semibold text-gray-800">{item.title}</span><span className="block text-xs text-gray-500 mt-0.5">{item.detail}</span></span>
                            <span className="relative inline-flex shrink-0"><input type="checkbox" className="peer sr-only" disabled={item.disabled} checked={!item.disabled&&Boolean((configData as any)[item.key])} onChange={e=>setConfigData(prev=>item.key==='posJornadaActiva'&&!e.target.checked?({...prev,posJornadaActiva:false,posArqueoEfectivo:false,posEmailCierreActivo:false}):({...prev,[item.key]:e.target.checked}))}/><span className="w-11 h-6 rounded-full bg-gray-300 peer-checked:bg-[#27481f] transition-colors"></span><span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5"></span></span>
                          </label>)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-3">Medios aceptados</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <i className="ri-money-dollar-circle-line text-green-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Efectivo</p>
                            <p className="text-xs text-gray-500">Pago presencial</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.efectivo}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, efectivo: e.target.checked, posArqueoEfectivo: e.target.checked ? prev.posArqueoEfectivo : false }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-bank-card-line text-blue-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Tarjeta crédito</p>
                            <p className="text-xs text-gray-500">Crédito</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.tarjetaCredito}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, tarjetaCredito: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <i className="ri-bank-card-2-line text-indigo-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Tarjeta débito</p>
                            <p className="text-xs text-gray-500">Débito</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.tarjetaDebito}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, tarjetaDebito: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <i className="ri-exchange-line text-amber-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Transferencia</p>
                            <p className="text-xs text-gray-500">Transferencia bancaria</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.transferencia}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, transferencia: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <i className="ri-more-2-line text-gray-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Otro</p>
                            <p className="text-xs text-gray-500">Cheque, depósito u otros</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.otroMetodo}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, otroMetodo: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-3">Comisiones</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Comisión tarjeta</p>
                            <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a pagos con tarjeta</p>
                          </div>
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-percent-line text-blue-700 text-xl"></i>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={configData.comisionTarjeta}
                            onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTarjeta: Number(e.target.value) }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      </div>

                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Comisión transferencia</p>
                            <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a transferencias</p>
                          </div>
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <i className="ri-percent-line text-amber-700 text-xl"></i>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={configData.comisionTransferencia}
                            onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTransferencia: Number(e.target.value) }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => {
              if (activeConfigModal !== 'modelo-operativo' && activeConfigModal !== 'flex-pass' && activeConfigModal !== 'planes-coaches' && activeConfigModal !== 'invitados' && activeConfigModal !== 'versiones-moviles') setActiveConfigModal(null);
            }}
          ></div>

          <div className={`relative bg-white rounded-xl shadow-xl transform transition-all w-full mx-auto ${activeConfigModal === 'control-acceso' || activeConfigModal === 'modelo-operativo' || activeConfigModal === 'flex-pass' || activeConfigModal === 'planes-coaches' || activeConfigModal === 'invitados' || activeConfigModal === 'versiones-moviles' || activeConfigModal === 'usuarios-admin' || activeConfigModal === 'centro-ayuda' ? 'max-w-5xl' : 'max-w-2xl'}`}>
            {getModalContent()}
            
            {activeConfigModal !== 'control-acceso' && activeConfigModal !== 'modelo-operativo' && activeConfigModal !== 'flex-pass' && activeConfigModal !== 'planes-coaches' && activeConfigModal !== 'invitados' && activeConfigModal !== 'versiones-moviles' && activeConfigModal !== 'centro-ayuda' && <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              {activeConfigModal === 'personalizacion' && (
                <button
                  onClick={handleResetBrandingDefaults}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Por defecto
                </button>
              )}
              <button 
                onClick={() => setActiveConfigModal(null)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  savingConfig ? 'bg-teal-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                <i className={`${savingConfig ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} mr-2`}></i>
                {savingConfig ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>}
          </div>
        </div>
      </div>
    );
  };

  const ToggleRow = ({
    title,
    desc,
    checked,
    onChange,
  }: {
    title: string;
    desc: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => {
    return (
      <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
        <div>
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <p className="text-xs text-gray-500 mt-1">{desc}</p>
        </div>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
        />
      </label>
    );
  };

  const handleLogout = async () => {
    if (confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      await signOut();
      navigate('/login');
    }
  };

  const handleSucursalChange = (sucursal: any) => {
    cambiarSucursal(sucursal);
    setShowSucursalSelector(false);
  };

  const handleOpenNewSucursal = () => {
    setSelectedSucursal(null);
    setSucursalForm(buildSucursalForm());
    setShowNewSucursalModal(true);
    setShowSucursalesModal(false);
  };

  const handleEditSucursal = (sucursal: any) => {
    setSelectedSucursal(sucursal);
    setSucursalForm(buildSucursalForm(sucursal));
    setShowEditSucursalModal(true);
    setShowSucursalesModal(false);
  };

  const handleDeleteSucursal = async (sucursal: any) => {
    if (!sucursal?.id) return;

    if (sucursalesDB.length <= 1) {
      alert('No puedes eliminar la única sucursal del gimnasio.');
      return;
    }

    const ok = confirm(
      `¿Eliminar la sucursal "${sucursal.nombre}"?\n\n` +
        'Solo se puede eliminar si no tiene datos asociados (usuarios, horarios, reservas, pagos, etc.).'
    );
    if (!ok) return;

    try {
      setDeletingSucursalId(sucursal.id);

      const [
        usuariosRes,
        horariosRes,
        cuposRes,
        feriadosRes,
        pagosRes,
        reservasRes,
      ] = await Promise.all([
        supabase.from('usuarios').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('horarios').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('configuracion_cupos').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('feriados').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('pagos').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase
          .from('reservas')
          .select('id, horarios!inner(sucursal_id)', { head: true, count: 'exact' })
          .eq('horarios.sucursal_id', sucursal.id),
      ]);

      const errors = [usuariosRes.error, horariosRes.error, cuposRes.error, feriadosRes.error, pagosRes.error, reservasRes.error].filter(Boolean);
      if (errors.length > 0) {
        console.error('Error validando eliminación de sucursal:', errors);
        alert('No se pudo validar la sucursal. Revisa permisos e intenta nuevamente.');
        return;
      }

      const bloqueos = [
        { label: 'usuarios', count: usuariosRes.count || 0 },
        { label: 'horarios', count: horariosRes.count || 0 },
        { label: 'configuración de cupos', count: cuposRes.count || 0 },
        { label: 'feriados', count: feriadosRes.count || 0 },
        { label: 'pagos', count: pagosRes.count || 0 },
        { label: 'reservas', count: reservasRes.count || 0 },
      ].filter((b) => b.count > 0);

      if (bloqueos.length > 0) {
        const detalle = bloqueos.map((b) => `- ${b.label}: ${b.count}`).join('\n');
        alert(
          'No puedes eliminar esta sucursal porque tiene datos asociados:\n\n' +
            `${detalle}\n\n` +
            'Primero limpia o migra esos datos.'
        );
        return;
      }

      const { error: deleteError } = await supabase.from('sucursales').delete().eq('id', sucursal.id);
      if (deleteError) {
        throw deleteError;
      }

      const nextSucursal =
        sucursalActiva?.id === sucursal.id
          ? sucursalesDB.find((s) => s.id !== sucursal.id) || null
          : null;

      await refreshSucursales();
      if (nextSucursal) {
        cambiarSucursal(nextSucursal as any);
      }
    } catch (error: any) {
      console.error('Error eliminando sucursal:', error);
      alert(error?.message || 'No se pudo eliminar la sucursal.');
    } finally {
      setDeletingSucursalId(null);
    }
  };

  const handleHorarioSucursalChange = (dia: DiaSemana, patch: Partial<HorarioDia>) => {
    setSucursalForm((prev) => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: {
          ...prev.horarios[dia],
          ...patch,
        },
      },
    }));
  };

  const handleSaveSucursal = async () => {
    const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId;
    if (!gimnasioId) {
      alert('No se pudo resolver el gimnasio para guardar la sucursal.');
      return;
    }

    const nombre = sucursalForm.nombre.trim();
    if (!nombre) {
      alert('El nombre de la sucursal es obligatorio.');
      return;
    }

    const payload = {
      gimnasio_id: gimnasioId,
      nombre,
      estado: sucursalForm.estado,
      direccion: sucursalForm.direccion.trim() || null,
      telefono: sucursalForm.telefono.trim() || null,
      email: sucursalForm.email.trim() || null,
      capacidad_maxima: Number(sucursalForm.capacidad_maxima) || null,
      horarios: sucursalForm.horarios,
      updated_at: new Date().toISOString(),
    };

    try {
      if (showNewSucursalModal) {
        const { data, error } = await supabase
          .from('sucursales')
          .insert([payload])
          .select('*')
          .single();

        if (error) throw error;

        await refreshSucursales();
        if (data) cambiarSucursal(data as any);
      } else if (selectedSucursal?.id) {
        const { error } = await supabase
          .from('sucursales')
          .update(payload)
          .eq('id', selectedSucursal.id);

        if (error) throw error;

        await refreshSucursales();
      }

      setShowEditSucursalModal(false);
      setShowNewSucursalModal(false);
      setSelectedSucursal(null);
      setSucursalForm(buildSucursalForm());
    } catch (error: any) {
      console.error('Error guardando sucursal:', error);
      alert(error?.message || 'No se pudo guardar la sucursal.');
    }
  };

  const sidebarBrandName =
    (sucursalActiva as any)?.gym_nombre ||
    configData.nombreGimnasio ||
    'MOWE';

  if (isEmbedView) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {isMobileViewport && sidebarOpen && (
        <button
          type="button"
          aria-label="Cerrar menú"
          className="fixed inset-0 z-[70] bg-black/45"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`${
          isMobileViewport
            ? `fixed inset-y-0 left-0 z-[80] w-72 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
            : sidebarOpen
              ? 'w-64'
              : 'w-20'
        } bg-gray-50 border-r border-gray-200 transition-all duration-300 flex flex-col shrink-0`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen && (
            <div className="flex items-center gap-2 min-w-0">
              {configData.logoUrl ? (
                <img
                  src={configData.logoUrl}
                  alt="Logo gimnasio"
                  className="h-8 w-8 rounded object-cover border border-gray-200 flex-shrink-0"
                />
              ) : null}
              <h1 className="text-xl font-bold text-teal-600 truncate">{sidebarBrandName}</h1>
            </div>
          )}
          {!sidebarOpen && configData.logoUrl ? (
            <img
              src={configData.logoUrl}
              alt="Logo gimnasio"
              className="h-8 w-8 rounded object-cover border border-gray-200"
            />
          ) : null}
          <button
            onClick={() => setSidebarOpen((prev) => !prev)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className={`${sidebarOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line'} text-xl text-gray-600`}></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="admin-sidebar-scroll flex-1 py-6 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  if (isMobileViewport) setSidebarOpen(false);
                }}
                className={`flex items-center px-4 py-3 mx-2 mb-1 rounded-lg transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-sky-200 text-sky-900 font-semibold ring-1 ring-inset ring-sky-300'
                    : 'text-gray-700 hover:bg-sky-100 hover:text-sky-900'
                }`}
              >
                <i className={`${item.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
                {sidebarOpen && <span className="ml-3 whitespace-nowrap">{item.label}</span>}
                {item.path === '/propuestas-ia' && familyProposalPendingCount > 0 && <span className={`${sidebarOpen ? 'ml-auto' : 'absolute right-2 top-1'} min-w-5 h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center`}>{familyProposalPendingCount > 99 ? '99+' : familyProposalPendingCount}</span>}
                {item.path === '/chat' && chatUnreadCount > 0 && <span className={`${sidebarOpen ? 'ml-auto' : 'absolute right-2 top-1'} min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center`}>{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Profile in Sidebar */}
        <div className="p-4 border-t border-gray-200 relative">
          <button
            onClick={() => setShowSidebarUserMenu(!showSidebarUserMenu)}
            className="w-full flex items-center gap-3 hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium flex-shrink-0">
              {profileData.avatar ? (
                <img src={profileData.avatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
              ) : (
                profile?.name?.charAt(0) || 'A'
              )}
            </div>
            {sidebarOpen && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{profileData.nombre}</p>
                  <p className="text-xs text-gray-500 truncate">{profileData.email}</p>
                </div>
                <i className={`ri-more-2-fill text-gray-400 flex-shrink-0 transition-transform ${showSidebarUserMenu ? 'rotate-90' : ''}`}></i>
              </>
            )}
          </button>

          {/* Sidebar User Menu Popup */}
          {showSidebarUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowSidebarUserMenu(false)}
              ></div>
              <div className={`absolute ${sidebarOpen ? 'left-2 right-2' : 'left-full ml-2 w-56'} bottom-full mb-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50`}>
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{profileData.nombre}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{profileData.cargo}</p>
                </div>
                <div className="py-1">
                  <button 
                    onClick={() => {
                      setShowProfileModal(true);
                      setShowSidebarUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-user-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                    Mi Perfil
                  </button>
                  {!isStaff && <button 
                    onClick={() => {
                      setShowConfig(true);
                      setShowSidebarUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-settings-3-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                    Configuración
                  </button>}
                </div>
                <div className="border-t border-gray-100 pt-1">
                  <button
                    onClick={() => {
                      setShowSidebarUserMenu(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    <i className="ri-logout-box-line w-5 h-5 flex items-center justify-center"></i>
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </>
          )}
        </div> {/* <-- Cierre del contenedor interno del sidebar */}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-gray-50 border-b border-gray-200 px-3 sm:px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              {isMobileViewport && (
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg hover:bg-gray-100"
                  aria-label="Abrir menú"
                >
                  <i className="ri-menu-line text-2xl text-gray-700"></i>
                </button>
              )}
              <h2 className="text-base md:text-lg font-semibold text-gray-800 truncate">
                {isStaff && location.pathname === '/progreso'
                  ? 'Panel del Entrenador'
                  : menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
              
              {/* Selector de Sucursal */}
              {!isStaff && <div className="relative">
                <button
                  onClick={() => setShowSucursalSelector(!showSucursalSelector)}
                  className="flex items-center gap-2 px-2.5 md:px-4 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer border border-teal-200 max-w-[11rem] sm:max-w-xs"
                >
                  <i className="ri-building-line text-lg"></i>
                  <span className="text-sm font-medium truncate">{sucursalActiva?.nombre || 'Seleccionar sucursal'}</span>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>

                {/* Dropdown de Sucursales */}
                {showSucursalSelector && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowSucursalSelector(false)}
                    ></div>
                    <div className="fixed left-3 right-3 mt-2 sm:absolute sm:left-0 sm:right-auto sm:w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-20">
                      <div className="p-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-gray-900">Seleccionar Sucursal</h3>
                          <button
                            onClick={() => {
                              setShowSucursalesModal(true);
                              setShowSucursalSelector(false);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer"
                            title="Gestionar sucursales"
                          >
                            <i className="ri-settings-3-line text-gray-600"></i>
                          </button>
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto p-2">
                        {sucursalesDB.map((sucursal) => (
                          <button
                            key={sucursal.id}
                            onClick={() => handleSucursalChange(sucursal)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                              sucursalActiva?.id === sucursal.id ? 'bg-teal-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                sucursalActiva?.id === sucursal.id ? 'bg-teal-600' : 'bg-gray-200'
                              }`}>
                                <i className={`ri-building-line text-lg ${
                                  sucursalActiva?.id === sucursal.id ? 'text-white' : 'text-gray-600'
                                }`}></i>
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-gray-900">{sucursal.nombre}</p>
                                <p className="text-xs text-gray-500">
                                  {[
                                    sucursal.direccion || 'Sin dirección',
                                    sucursal.gym_nombre ? `Gym: ${sucursal.gym_nombre}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                              </div>
                            </div>
                            {sucursalActiva?.id === sucursal.id && (
                              <i className="ri-check-line text-teal-600 text-lg"></i>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setShowSucursalesModal(true);
                            setShowSucursalSelector(false);
                          }}
                          className="w-full py-2 text-sm text-teal-600 font-medium hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Gestionar Sucursales
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>}
            </div>
            <div className="flex items-center gap-4">
              {/* Notifications Button */}
              {!isCoach && <div className="relative">
                <button 
                  onClick={() => {
                    const next = !showNotifications;
                    setShowNotifications(next);
                    setShowConfig(false);
                    if (next) void loadNotificaciones();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer relative"
                >
                  <i className="ri-notification-line text-xl text-gray-600"></i>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Notificaciones</h3>
                        <div className="flex items-center gap-2">
                          {unreadCount > 0 && (
                            <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                              {unreadCount} nuevas
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => void marcarTodasNotificacionesLeidas()}
                            disabled={unreadCount === 0 || marcandoTodasNotificaciones}
                            className="text-xs font-medium text-teal-700 hover:text-teal-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            <i className={`${marcandoTodasNotificaciones ? 'ri-loader-4-line animate-spin' : 'ri-check-double-line'} mr-1`}></i>
                            {marcandoTodasNotificaciones ? 'Marcando...' : 'Marcar todas como leídas'}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notificacionesLoading && (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          <p className="mt-2 text-sm">Cargando notificaciones...</p>
                        </div>
                      )}

                      {!notificacionesLoading && notificacionesError && (
                        <div className="p-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
                          {notificacionesError}
                        </div>
                      )}

                      {!notificacionesLoading && !notificacionesError && notificaciones.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-notification-off-line text-2xl"></i>
                          <p className="mt-2 text-sm">No hay notificaciones</p>
                        </div>
                      )}

                      {!notificacionesLoading && !notificacionesError && notificaciones.map((notif) => (
                        <button
                          type="button"
                          key={notif.id}
                          onClick={() => {
                            if (!notif.leida) void marcarNotificacionLeida(notif.id);
                          }}
                          className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                            !notif.leida ? 'bg-teal-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <i className={`${getNotificationIcon(notif.tipo)} text-lg`}></i>
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900">{notif.titulo}</p>
                                {!notif.leida && <span className="w-2 h-2 bg-teal-500 rounded-full"></span>}
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5">{notif.mensaje}</p>
                              <p className="text-xs text-gray-400 mt-1">{notif.tiempo}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="p-3 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          setShowNotifications(false);
                          navigate('/bandeja-notificaciones');
                        }}
                        className="w-full py-2 text-sm text-teal-600 font-medium hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      >
                        Ver todas las notificaciones
                      </button>
                    </div>
                  </div>
                )}
              </div>}

              {/* Staff only sees role-specific help; managers retain full configuration. */}
              <div className="relative">
                <button 
                  onClick={() => {
                    setShowConfig(!showConfig);
                    setShowNotifications(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-settings-3-line text-xl text-gray-600"></i>
                </button>

                {/* Settings Dropdown */}
                {showConfig && (
                  <div className="absolute right-0 mt-2 w-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">{isStaff ? 'Ayuda' : 'Configuración'}</h3>
                      <p className="text-sm text-gray-500 mt-1">{isStaff ? 'Consulta las guías disponibles para tu perfil' : 'Administra todas las opciones del sistema'}</p>
                    </div>
                    <div className="max-h-[480px] overflow-y-auto p-4">
                      {visibleConfigurationOptions.map((categoria) => (
                        <div key={categoria.categoria} className="mb-4 last:mb-0">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                            {categoria.categoria}
                          </p>
                          <div className="space-y-1">
                            {categoria.items.map((item) => (
                              <button
                                key={item.titulo}
                                onClick={() => handleConfigItemClick(item.id)}
                                className="w-full flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-left"
                              >
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                                  <i className={`${item.icon} text-teal-600 text-lg`}></i>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900">{item.titulo}</p>
                                  <p className="text-xs text-gray-500">{item.descripcion}</p>
                                </div>
                                <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Click outside to close dropdowns */}
        {(showNotifications || showConfig) && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => {
              setShowNotifications(false);
              setShowConfig(false);
            }}
          ></div>
        )}

        {/* Page Content */}
        <main className={`flex-1 min-h-0 p-3 sm:p-4 md:p-6 ${location.pathname === '/chat' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {children}
        </main>
      </div>

      {/* Configuration Modals */}
      {!isStaff && renderConfigModal()}

      {/* Modal de Gestión de Sucursales */}
      {showSucursalesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowSucursalesModal(false)}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-4xl mx-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Gestión de Sucursales</h3>
                    <p className="text-sm text-gray-500 mt-1">Administra todas las ubicaciones del gimnasio</p>
                  </div>
                  <button
                    onClick={handleOpenNewSucursal}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Nueva Sucursal
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sucursalesDB.map((sucursal) => (
                    <div key={sucursal.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                            <i className="ri-building-line text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">{sucursal.nombre}</h4>
                            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${
                              sucursal.estado === 'activo' ? 'bg-green-100 text-green-700' :
                              sucursal.estado === 'mantenimiento' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {sucursal.estado === 'activo' ? 'Activo' : 
                               sucursal.estado === 'mantenimiento' ? 'Mantenimiento' : 'Inactivo'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleEditSucursal(sucursal)}
                          className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                        >
                          <i className="ri-edit-line text-gray-600"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteSucursal(sucursal)}
                          disabled={deletingSucursalId === sucursal.id}
                          className="p-2 hover:bg-red-50 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Eliminar sucursal"
                        >
                          <i className="ri-delete-bin-line text-red-600"></i>
                        </button>
                      </div>

                      <div className="space-y-2 text-sm">
                        {sucursal.direccion && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <i className="ri-map-pin-line text-gray-400"></i>
                            <span>{sucursal.direccion}</span>
                          </div>
                        )}
                        {sucursal.telefono && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <i className="ri-phone-line text-gray-400"></i>
                            <span>{sucursal.telefono}</span>
                          </div>
                        )}
                        {sucursal.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <i className="ri-mail-line text-gray-400"></i>
                            <span>{sucursal.email}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-xs text-gray-500">Usuarios Activos</p>
                          <p className="text-lg font-semibold text-gray-900">{sucursal.usuarios_activos || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Capacidad Máxima</p>
                          <p className="text-lg font-semibold text-gray-900">{sucursal.capacidad_maxima || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={() => setShowSucursalesModal(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar/Nueva Sucursal */}
      {(showEditSucursalModal || showNewSucursalModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => {
                setShowEditSucursalModal(false);
                setShowNewSucursalModal(false);
                setSelectedSucursal(null);
                setSucursalForm(buildSucursalForm());
              }}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-3xl mx-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {showNewSucursalModal ? 'Nueva Sucursal' : 'Editar Sucursal'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {showNewSucursalModal ? 'Agrega una nueva ubicación' : 'Modifica la información de la sucursal'}
                </p>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Sucursal</label>
                      <input
                        type="text"
                        value={sucursalForm.nombre}
                        onChange={(e) => setSucursalForm((prev) => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: PowerFit Centro"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                      <select
                        value={sucursalForm.estado}
                        onChange={(e) =>
                          setSucursalForm((prev) => ({
                            ...prev,
                            estado: e.target.value as 'activo' | 'mantenimiento' | 'inactivo',
                          }))
                        }
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="activo">Activo</option>
                        <option value="mantenimiento">Mantenimiento</option>
                        <option value="inactivo">Inactivo</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                    <input
                      type="text"
                      value={sucursalForm.direccion}
                      onChange={(e) => setSucursalForm((prev) => ({ ...prev, direccion: e.target.value }))}
                      placeholder="Av. Principal 123, Santiago"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={sucursalForm.telefono}
                        onChange={(e) => setSucursalForm((prev) => ({ ...prev, telefono: e.target.value }))}
                        placeholder="+56 9 8765 4321"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={sucursalForm.email}
                        onChange={(e) => setSucursalForm((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="sucursal@powerfit.cl"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Capacidad Máxima</label>
                    <input
                      type="number"
                      value={sucursalForm.capacidad_maxima}
                      onChange={(e) => setSucursalForm((prev) => ({ ...prev, capacidad_maxima: e.target.value }))}
                      min="50"
                      max="500"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Horarios de Atención</label>
                    <div className="space-y-3">
                      {DIAS_SEMANA.map((dia) => {
                        const horario = sucursalForm.horarios[dia];
                        return (
                          <div key={dia} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                            <div className="w-28">
                              <span className="text-sm font-medium text-gray-700 capitalize">{dia}</span>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!horario.cerrado}
                                onChange={(e) => handleHorarioSucursalChange(dia, { cerrado: !e.target.checked })}
                                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                              />
                              <span className="text-sm text-gray-600">Abierto</span>
                            </label>
                            <input
                              type="time"
                              value={horario.apertura}
                              onChange={(e) => handleHorarioSucursalChange(dia, { apertura: e.target.value })}
                              disabled={horario.cerrado}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            />
                            <span className="text-gray-400">-</span>
                            <input
                              type="time"
                              value={horario.cierre}
                              onChange={(e) => handleHorarioSucursalChange(dia, { cierre: e.target.value })}
                              disabled={horario.cerrado}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={() => {
                    setShowEditSucursalModal(false);
                    setShowNewSucursalModal(false);
                    setSelectedSucursal(null);
                    setSucursalForm(buildSucursalForm());
                  }}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveSucursal}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-save-line mr-2"></i>
                  {showNewSucursalModal ? 'Crear Sucursal' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mi Perfil */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowProfileModal(false)}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Mi Perfil</h3>
                    <p className="text-sm text-gray-500 mt-1">Gestiona tu información personal</p>
                  </div>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 pt-4 border-b border-gray-200">
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setProfileTab('info');
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                      profileTab === 'info' 
                        ? 'border-teal-500 text-teal-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Información Personal
                  </button>
                  <button
                    onClick={() => {
                      setProfileTab('password');
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                      profileTab === 'password' 
                        ? 'border-teal-500 text-teal-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Cambiar Contraseña
                  </button>
                </div>
              </div>

              <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
                {profileTab === 'info' ? (
                  <div className="space-y-6">
                    {/* Avatar con Drag & Drop */}
                    <div 
                      className={`flex items-center gap-6 p-4 rounded-xl border-2 border-dashed transition-all ${
                        isDraggingAvatar 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-300 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                      onDragOver={handleAvatarDragOver}
                      onDragLeave={handleAvatarDragLeave}
                      onDrop={handleAvatarDrop}
                    >
                      <div 
                        className={`w-24 h-24 rounded-full flex items-center justify-center overflow-hidden cursor-pointer transition-all ${
                          isDraggingAvatar ? 'ring-4 ring-teal-300 scale-105' : 'bg-teal-100'
                        }`}
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        {profileData.avatar ? (
                          <img src={profileData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                        ) : isDraggingAvatar ? (
                          <i className="ri-download-2-line text-4xl text-teal-600"></i>
                        ) : (
                          <span className="text-4xl font-bold text-teal-600">
                            {profileData.nombre.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={avatarInputRef}
                          onChange={handleAvatarUpload}
                          accept="image/png,image/jpeg,image/webp"
                          capture="user"
                          className="hidden"
                        />
                        {isDraggingAvatar ? (
                          <div className="text-center">
                            <p className="text-sm font-medium text-teal-700">¡Suelta la imagen aquí!</p>
                            <p className="text-xs text-teal-600 mt-1">La imagen se cargará automáticamente</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-700 mb-1">Foto de Perfil</p>
                            <p className="text-xs text-gray-500 mb-3">Arrastra una imagen o usa los botones</p>
                            <div className="flex gap-2">
                              <button 
                                onClick={() => avatarInputRef.current?.click()}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                              >
                                <i className="ri-upload-2-line mr-2"></i>
                                {avatarUploading ? 'Procesando…' : profileData.avatar ? 'Cambiar Foto' : 'Subir Foto'}
                              </button>
                              {profileData.avatar && (
                                <button 
                                  onClick={() => setProfileData(prev => ({ ...prev, avatar: '' }))}
                                  className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                                >
                                  <i className="ri-delete-bin-line mr-2"></i>
                                  Eliminar
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">Puedes tomarla con la cámara o subir JPG, PNG o WEBP. Se optimiza a un máximo de 500 KB.</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Información */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nombre Completo</label>
                        <input
                          type="text"
                          value={profileData.nombre}
                          onChange={(e) => setProfileData(prev => ({ ...prev, nombre: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                        <input
                          type="tel"
                          value={profileData.telefono}
                          onChange={(e) => setProfileData(prev => ({ ...prev, telefono: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                        <input
                          type="text"
                          value={profileData.cargo}
                          disabled
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                        />
                      </div>
                    </div>

                    {/* Info adicional */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Información de la Cuenta</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Fecha de Ingreso</p>
                          <p className="font-medium text-gray-900">
                            {new Date(profileData.fechaIngreso).toLocaleDateString('es-CL', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Rol</p>
                          <p className="font-medium text-gray-900">{profileData.cargo}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <i className="ri-shield-keyhole-line text-amber-600 text-xl mt-0.5"></i>
                        <div>
                      <p className="text-sm font-medium text-amber-800">Seguridad de la Contraseña</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Tu contraseña debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas y números.
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Para proteger tu cuenta, debes confirmar la contraseña actual antes de realizar el cambio.
                          </p>
                        </div>
                      </div>
                    </div>

                    {passwordError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        {passwordError}
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                        {passwordSuccess}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña Actual</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <i className="ri-shield-keyhole-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <i className="ri-lock-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Nueva Contraseña</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <i className="ri-lock-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={profileTab === 'info' ? handleSaveProfile : handleChangePassword}
                  disabled={(profileTab === 'password' && passwordSaving) || profileSaving || avatarUploading}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-2"></i>
                  {profileTab === 'info' ? profileSaving ? 'Guardando…' : 'Guardar Cambios' : passwordSaving ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NumberField({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (value: number) => void }) {
  return (
    <label className="text-sm font-medium text-gray-700">
      {label}
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Math.min(max, Math.max(min, Number(event.target.value) || min)))}
        className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5"
      />
    </label>
  );
}







